import React, { useState, useEffect, useRef, useMemo } from 'react';
import { VacancyStep, VacancyData, Vacancy, UserRole, VacancyTone, VacancyTemplates, TemplateItem, TemplatePool, ABTestVariant, StepGovernance, WerfplaatsConfig, HiringPlan } from '../types';
import UsageStatsBar from './UsageStatsBar';

import VacancyPremiumJobboards from './VacancyPremiumJobboards';
import { isClientPortalHostname } from '../utils/isClientPortalHost';
import VacancyNetworkShare from './VacancyNetworkShare';
import VacancyTeamChoice from './VacancyTeamChoice';
import {
  generateMarketResearch, generatePersona,
  generateStepCommentary,
  generateMarketInsights, generateStructuredTemplates,
  refineTemplateSpecificly,
  performRealMatch,
  generate_vacancy_from_research
} from '../services/geminiService';
import {
  supabase_update, supabase_get_client_branding, supabase,
  supabase_get_talent_pool, supabase_submit_application,
  supabase_get_clients, supabase_update_client
} from '../services/supabaseService';
import { appendVacancyFlowStepHistory, type VacancyFlowActor } from '../utils/vacancyFlowHistory';
import { describeVacancySnapshotChanges, type VacancySnapshotForDiff } from '../utils/vacancyChangeDiff';
import {
  mergeIntakeSnapshotIntoVacancy,
  pickIntakeKeysFromData,
  appendIntakeVersionHistory,
  intakeBaselineSnapshotFromVacancy,
  lastIntakeHistorySnapshot,
  pickIntakeSnapshotFromUpdates,
  intakeSnapshotsEqual,
  snapshotToVacancySnapshotForDiff,
  type VacancyIntakeSnapshot,
  type VacancyIntakeVersionEntry,
  intakeBriefingTextFromVacancy,
} from '../utils/vacancyIntakeVersions';
import { intakeSnapshotTextDiffSummaries, mergeStructuralAndTextDiffLines } from '../utils/intakeTextDiff';
import PublicVacancyView from './PublicVacancyView';
import VacancyIntake, { type VacancyIntakeHandle } from './VacancyIntake';
import IntakeVersionHistoryModal from './IntakeVersionHistoryModal';
import MarketResearchOverview from './MarketResearchOverview';
import AiProcessingState from './AiProcessingState';
import KanbanBoard from './KanbanBoard';
import VacancyCandidates from './VacancyCandidates';
import VacancySourcing from './VacancySourcing';
import InterviewFlow from './InterviewFlow';
import WerftoolsLogo from './WerftoolsLogo';
import {
  ChevronRight, ChevronLeft, Sparkles,
  Target, Eye, MessageSquare, TrendingUp,
  Database, Zap, Wand2, Coffee, Briefcase, BrainCircuit,
  CheckCircle, Loader2, Copy, Edit3, Check, ClipboardCheck,
  Layout, LayoutTemplate, BarChart, ExternalLink, ShieldCheck,
  AlertCircle, RefreshCcw, FileCheck, Send, Home, AlertTriangle, RefreshCw,
  Share2, Mail, FileText, Star, Trophy, Save, RotateCcw,
  LayoutGrid, Maximize2, Globe, Monitor, Split, PlayCircle, StopCircle, Linkedin,
  Plus, Search, Activity, Lock, Unlock, ShieldAlert, RotateCw, ArrowLeft, Users, MessageCircle, Settings, BarChart2, User, Clock, Calendar, List, Trash2, X, Columns, History
} from 'lucide-react';
import AiRichTextEditor from './AiRichTextEditor';
import StageConfigurator from './StageConfigurator';
import VacancyAnalytics from './VacancyAnalytics';
import TodoWidget from './TodoWidget';
import ShareModal from './ShareModal';
import CallWerfplaatsModal from './CallWerfplaatsModal';

interface VacancyFlowProps {
  vacancy: Vacancy;
  onBack: () => void;
  userRole?: UserRole;
  userCredits?: number;
  clientId?: string;
  onUpdateCredits?: (newTotal: number) => void;
  isReviewer?: boolean;
  initialStep?: VacancyStep; 
  onVacancyUpdate?: (updatedVacancy: Vacancy) => void;
  isEmbedded?: boolean;
  hideHeader?: boolean;
  allowedSteps?: VacancyStep[];
  /** For activity / audit: who opened steps or saved intake */
  actorUser?: { id?: string; name?: string; email?: string };
}

const PHASES = [
  { name: 'Strategie', range: [0, 1, 2, 3, 4, 5] }, // Intake, Research, Builder, Team, Platforms, Publish
];

const TONE_OPTIONS: { value: VacancyTone; label: string; icon: any }[] = [
  { value: 'Professional', label: 'Zakelijk', icon: <Briefcase size={14} /> },
  { value: 'Friendly', label: 'Vriendelijk', icon: <Coffee size={14} /> },
  { value: 'Creative', label: 'Creatief', icon: <Sparkles size={14} /> },
  { value: 'Direct', label: 'Direct', icon: <Target size={14} /> }
];

// Helper for "Restart AI" button
const RegenerateButton = ({ onClick, isLoading, label = "Herstart Froukje" }: { onClick: () => void, isLoading: boolean, label?: string }) => (
  <button
    onClick={onClick}
    disabled={isLoading}
    className="flex items-center gap-2 bg-white text-slate-600 border border-slate-200 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 hover:text-slate-900 transition-all shadow-sm active:scale-95 disabled:opacity-50"
  >
    <RotateCw size={12} className={isLoading ? "animate-spin" : ""} />
    {isLoading ? "Bezig..." : label}
  </button>
);

const VacancyFlow: React.FC<VacancyFlowProps> = ({ 
  vacancy, onBack, userRole = UserRole.RECRUITER, userCredits = 100, 
  clientId, onUpdateCredits, isReviewer = false, initialStep, onVacancyUpdate,
  isEmbedded = false, hideHeader = false, allowedSteps, actorUser
}) => {
  const ALL_STEPS = [
    { id: VacancyStep.INTAKE, icon: <Briefcase size={18} />, label: '1. Intake' },
    { id: VacancyStep.RESEARCH, icon: <Search size={18} />, label: '2. Onderzoek & Persona' },
    { id: VacancyStep.COMM_TEMPLATES, icon: <LayoutTemplate size={18} />, label: '3. Vacature Builder' },
    { id: VacancyStep.STRATEGY, icon: <Users size={18} />, label: '4. Team voor Vacature' },
    { id: VacancyStep.PREMIUM_JOB_BOARDS, icon: <TrendingUp size={18} />, label: '5. Premium Job Boards' },
    { id: VacancyStep.PUT_LIVE, icon: <Globe size={18} />, label: '6. Vacature Live Zetten' }
  ];

  const STEPS = allowedSteps 
    ? ALL_STEPS.filter(s => allowedSteps.includes(s.id as VacancyStep)) 
    : ALL_STEPS;

  const DYNAMIC_PHASES = allowedSteps 
    ? [{ name: 'Stappen', range: Array.from({ length: STEPS.length }, (_, i) => i) }] 
    : PHASES;

  // --- GOVERNANCE LOGIC ---
  const [governance, setGovernance] = useState<Record<string, StepGovernance>>({});
  const [isStaff, setIsStaff] = useState(userRole === UserRole.ADMIN || userRole === UserRole.RECRUITER);
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [clientSettings, setClientSettings] = useState<any>(null);

  useEffect(() => {
    setIsStaff(userRole === UserRole.ADMIN || userRole === UserRole.RECRUITER);
  }, [userRole]);

  const [isEnterprise, setIsEnterprise] = useState(false);

  useEffect(() => {
    const loadGovAndClient = async () => {
      const branding = await supabase_get_client_branding(vacancy.clientId);
      if (branding?.stepGovernance) {
        setGovernance(branding.stepGovernance);
      }

      const clients = await supabase_get_clients(vacancy.clientId);
      if (clients && clients.length > 0) {
        setIsEnterprise(clients.find(c => c.id === vacancy.clientId)?.plan_tier === 'Enterprise');
      }

      const { supabase_get_client_settings } = await import('../services/supabaseService');
      const settings = await supabase_get_client_settings(vacancy.clientId);
      if (settings) {
          setClientSettings(settings);
      }
    };
    loadGovAndClient();
  }, [vacancy.clientId]);

  const canSeeStep = (stepId: string) => {
    if (isClientPortalHostname() && stepId === VacancyStep.STRATEGY) return false;
    if (isStaff) return true;
    if (isEnterprise && stepId === VacancyStep.STRATEGY) return false;
    const config = governance[stepId];
    if (!config) return true;
    if (config.visibility === 'werfplaats') return false;
    if (config.visibility === 'recruiters') {
      return userRole === UserRole.CLIENT_RECRUITER || userRole === UserRole.CLIENT;
    }

    return true;
  };

  const isStepReadOnly = (stepId: string) => {
    if (isStaff) return false;
    const config = governance[stepId];
    if (!config) return false;
    return config.mode === 'results';
  };

  let derivedInitialStep = initialStep;
  if (!derivedInitialStep && vacancy.data?.publishedToSite && STEPS.some(s => s.id === VacancyStep.PUT_LIVE)) {
    derivedInitialStep = VacancyStep.PUT_LIVE;
  }

  const initialStepIndexRaw = derivedInitialStep
    ? STEPS.findIndex(s => s.id === derivedInitialStep)
    : Math.max(0, STEPS.findIndex(s => s.id === (vacancy.data as any)?.flowStep));
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndexRaw !== -1 ? initialStepIndexRaw : 0);
  const [collapsedPhases, setCollapsedPhases] = useState<number[]>([]);
  const [showCallWerfplaats, setShowCallWerfplaats] = useState(false);

  useEffect(() => {
    if (STEPS[currentStepIndex]?.id === VacancyStep.SHORTLIST || STEPS[currentStepIndex]?.id === VacancyStep.INTERVIEW_KANBAN) {
      const activePhaseObj = DYNAMIC_PHASES.findIndex(p => p.range.includes(currentStepIndex));
      const toCollapse = DYNAMIC_PHASES.map((_, i) => i).filter(i => i !== activePhaseObj);
      setCollapsedPhases(toCollapse);
    } else {
      setCollapsedPhases([]);
    }
  }, [currentStepIndex]);

  useEffect(() => {
    if (STEPS[currentStepIndex] && !canSeeStep(STEPS[currentStepIndex].id)) {
      const firstVisible = STEPS.findIndex(s => canSeeStep(s.id));
      if (firstVisible !== -1) setCurrentStepIndex(firstVisible);
    }
  }, [governance, userRole]);

  // FIX: Consider started if *any* improved data exists or status > INTAKE or currentStepIndex > INTAKE
  const [isStarted, setIsStarted] = useState(
    !!vacancy?.data?.marketResearch ||
    !!vacancy?.data?.persona ||
    (STEPS.findIndex(s => s.id === (vacancy.data as any)?.flowStep) > STEPS.findIndex(s => s.id === VacancyStep.INTAKE)) ||
    (initialStepIndexRaw > STEPS.findIndex(s => s.id === VacancyStep.INTAKE))
  );
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [engineError, setEngineError] = useState<string | null>(null);
  const [showGeminiExhaustedModal, setShowGeminiExhaustedModal] = useState(false);
  const [isAutoProcessing, setIsAutoProcessing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [processingLogs, setProcessingLogs] = useState<string[]>([]);
  const [stepCommentary, setStepCommentary] = useState<Record<string, string>>({});
  const [isCommentaryLoading, setIsCommentaryLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [promptMode, setPromptMode] = useState<'research' | 'persona' | null>(null);
  const [aiPromptText, setAiPromptText] = useState('');
  const [standardSections, setStandardSections] = useState<any[]>([]);

  useEffect(() => {
    if (vacancy.clientId) {
        supabase.from('automation_templates')
          .select('id, name, content')
          .eq('client_id', vacancy.clientId)
          .eq('type', 'Section')
          .then(({ data }) => setStandardSections(data || []));
    }
  }, [vacancy.clientId]);

  const [templateCategory, setTemplateCategory] = useState<'jobText' | 'linkedinPost' | 'inMail' | 'whatsappMessage'>('linkedinPost');

  // Sjabloon modals
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showSplitView, setShowSplitView] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateOverwriteId, setSaveTemplateOverwriteId] = useState<string | null>(null);
  const [existingTemplates, setExistingTemplates] = useState<any[]>([]);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [showTemplatesPickerModal, setShowTemplatesPickerModal] = useState(false);
  const [templatePickerList, setTemplatePickerList] = useState<any[]>([]);
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [isGeneratingMarketing, setIsGeneratingMarketing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState<string | null>(null);
  const [showStageConfig, setShowStageConfig] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showTodos, setShowTodos] = useState(false);
  const [builderSection, setBuilderSection] = useState(-1); // -1 = full preview, 0+ = section index
  const [userId, setUserId] = useState<string>('');
  const [currentTitle, setCurrentTitle] = useState(vacancy.title);
  const [shouldAutoStartEngine, setShouldAutoStartEngine] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showPathChoiceModal, setShowPathChoiceModal] = useState(false);
  const [showIntakeHistoryModal, setShowIntakeHistoryModal] = useState(false);
  const [contactPersons, setContactPersons] = useState<{ id: string, name: string, email: string }[]>([]);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const { data: authData } = await supabase.auth.getUser();
        if (!authData.user) return;
        
        const { data: profile } = await supabase.from('profiles').select('client_id').eq('id', authData.user.id).single();
        const userClientId = profile?.client_id;
        if (!userClientId) return;

        const { data } = await supabase.from('profiles').select('id, name, email').eq('client_id', userClientId);
        if (data) setContactPersons(data);
      } catch (e) {
        console.error("Failed to load recruiters list", e);
      }
    };
    fetchContacts();
  }, []);

  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getInitialData = (v: Vacancy) => {
    const rawData = v.data || {};
    return {
      marketResearch: '',
      persona: '',
      personaConfig: undefined,
      contactPersonId: rawData.contactPersonId || '',
      marketingConfig: undefined,
      linkedinSearchString: '',
      metaAdsCopy: '',
      toneAndSeo: '',
      structuredTemplates: undefined,
      marketingStrategy: '',
      massMarketingAdvice: '',
      verificationReport: '',
      publishedToSite: false,
      publishDate: undefined,
      abTestVariants: [],
      abTestStatus: (rawData.abTestStatus as any) || 'draft',
      activePlatforms: { careerSite: false, linkedin: false, indeed: false, instagram: false },
      ...rawData,
      // Ensure complex objects and fallbacks are preserved AFTER spreading rawData
      finalVacancyContent: rawData.finalVacancyContent || v.description || '',
      marketResearchInsights: rawData.marketResearchInsights || [],
      personas: rawData.personas || [],
      tone: (rawData.tone as VacancyTone) || 'Professional',
      claimStatus: rawData.claimStatus || 'Open'
    };
  };

  const [data, setData] = useState<Partial<VacancyData>>(getInitialData(vacancy));
  const latestDataRef = useRef(data);
  const intakeStepRef = useRef<VacancyIntakeHandle | null>(null);
  /** Last intake snapshot for Vacature Builder / sync checkpoints (aligned with vacancy history when present). */
  const lastIntakeVersionAnchorRef = useRef<VacancyIntakeSnapshot | null>(null);
  /** Baseline for “what changed” logs on sync (not every tab switch). */
  const lastPersistedAuditRef = useRef<VacancySnapshotForDiff | null>(null);
  useEffect(() => {
    latestDataRef.current = data;
  }, [data]);

  // Strategy Step State
  const [strategyChoice, setStrategyChoice] = useState<'werfplaats' | 'self' | null>(
    vacancy.data?.werfplaatsConfig ? 'werfplaats' :
      vacancy.data?.hiringPlan ? 'self' : null
  );
  const [werfplaatsConfig, setWerfplaatsConfig] = useState<WerfplaatsConfig | null>(vacancy.data?.werfplaatsConfig || null);
  const [hiringPlan, setHiringPlan] = useState<HiringPlan | null>(vacancy.data?.hiringPlan || null);

  useEffect(() => {
    setCurrentTitle(vacancy.title);
    // Reset data when switching vacancies
    setData(getInitialData(vacancy));
    lastPersistedAuditRef.current = {
      title: vacancy.title || '',
      description: vacancy.description || '',
      data: { ...(vacancy.data as any) },
      opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
      eindklant_id: vacancy.eindklant_id ?? null,
      shared_with_users: vacancy.shared_with_users,
      shared_with_clients: vacancy.shared_with_clients,
      status: (vacancy as any).status ?? null,
    };
  }, [vacancy.id]);

  // Removed destructive useEffect that synced vacancy.data directly and overwrote local state while editing.
  // The initial state uses `vacancy` on mount, which is correct. Updates are tracked via autosave.

  const COST_ESTIMATE = 150;

  useEffect(() => {
    if (isClientPortalHostname()) return;
    if (isStarted && vacancy?.id && !isAutoProcessing) {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = setTimeout(() => {
        handleSupabaseSync(true);
      }, 5000);
    }

    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [data, currentStepIndex, currentTitle]); // Added currentTitle to deps

  const handleBack = async () => {
    await handleSupabaseSync(true);
    onBack();
  };

  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data.user) setUserId(data.user.id);
    };
    getUser();
  }, []);

  useEffect(() => {
    const step = STEPS[currentStepIndex];
    if (step && isStarted && !isAutoProcessing && !stepCommentary[step.id]) {
      updateCommentary(step.id);
    }
  }, [currentStepIndex, isStarted, isAutoProcessing]);

  const updateCommentary = async (stepId: string) => {
    setIsCommentaryLoading(true);
    
    // Fallback dictionary instead of burning Gemini API quotas on flavor text
    const localCommentary: Record<string, string> = {
      [VacancyStep.INTAKE]: "Laten we beginnen met de basis. Wat voor topper zoeken we?",
      [VacancyStep.RESEARCH]: "Ik heb het web afgespeurd. Kijk eens wat een interessante data ik heb gevonden voor deze doelgroep!",
      [VacancyStep.COMM_TEMPLATES]: "Hier zijn de gegenereerde teksten! Ik heb ze alvast geschreven met jouw ideale kandidaat in gedachten.",
      [VacancyStep.STRATEGY]: "Tijd voor het echte werk. Wie in het team pakt welke rol op binnen de sollicitatieprocedure?",
      [VacancyStep.PREMIUM_JOB_BOARDS]: "Laten we deze vacature een boost geven en uitzetten op de kanalen waar jouw persona zich bevindt.",
      [VacancyStep.PUT_LIVE]: "Klaar voor de start? Bekijk het resultaat en zet de vacature live zodat de kandidaten kunnen binnenstromen!",
      [VacancyStep.SHORTLIST]: "Spannend! Dit is de selectie van talenten die het best aansluiten op jouw vacature.",
      [VacancyStep.INTERVIEW_KANBAN]: "Tijd voor de gesprekken. Sleep de kandidaten door de funnel om het overzicht te bewaren."
    };

    setTimeout(() => {
      setStepCommentary(prev => ({ 
        ...prev, 
        [stepId]: localCommentary[stepId] || "Laten we hier even kritisch naar kijken." 
      }));
      setIsCommentaryLoading(false);
    }, 400); // Small fake delay for UX
  };

  // Helper: built sections > finalVacancyContent > intake briefing (data.vacancyText) > description kolom
  const buildDescriptionHTML = (currentData: any): string => {
    if (currentData.builtVacancySections && currentData.builtVacancySections.length > 0) {
      return currentData.builtVacancySections.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n');
    }
    const built = String(currentData.finalVacancyContent || '').trim();
    if (built) return currentData.finalVacancyContent;
    return (
      intakeBriefingTextFromVacancy({
        description: vacancy.description,
        data: { ...(vacancy.data as Record<string, unknown>), ...currentData },
      }) ||
      vacancy.description ||
      ''
    );
  };

  const resolveDescriptionForPersist = (currentData: any): string => {
    let html = buildDescriptionHTML(currentData);
    if (!String(html || '').trim()) {
      const briefing = intakeBriefingTextFromVacancy(vacancy).trim();
      if (briefing) html = briefing;
    }
    if (!String(html || '').trim() && String(lastPersistedAuditRef.current?.description || '').trim()) {
      html = String(lastPersistedAuditRef.current?.description);
    }
    return html;
  };

  const intakeVersionsChronological = useMemo(() => {
    const h = (vacancy.data as any)?.intakeVersionHistory;
    if (!Array.isArray(h)) return [] as VacancyIntakeVersionEntry[];
    return [...h] as VacancyIntakeVersionEntry[];
  }, [vacancy.id, JSON.stringify((vacancy.data as any)?.intakeVersionHistory || [])]);

  const intakeVersionsNewestFirst = useMemo(
    () => [...intakeVersionsChronological].reverse(),
    [intakeVersionsChronological]
  );

  useEffect(() => {
    const snap =
      lastIntakeHistorySnapshot((vacancy.data as Record<string, unknown>) || undefined) ??
      intakeBaselineSnapshotFromVacancy(vacancy as Vacancy);
    lastIntakeVersionAnchorRef.current = snap;
  }, [vacancy.id, JSON.stringify((vacancy.data as any)?.intakeVersionHistory || [])]);

  const builderPendingDiffSnap: VacancySnapshotForDiff = useMemo(
    () => ({
      title: currentTitle,
      description: buildDescriptionHTML(data),
      data: { ...(vacancy.data || {}), ...(data as any) } as Record<string, unknown>,
      opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
      eindklant_id: vacancy.eindklant_id ?? null,
      shared_with_users: vacancy.shared_with_users,
      shared_with_clients: vacancy.shared_with_clients,
      status: (vacancy as any).status ?? null,
    }),
    [
      currentTitle,
      vacancy,
      data,
    ]
  );

  const restoreIntakeFromHistoryInFlow = (entry: VacancyIntakeVersionEntry) => {
    const snap = entry.snapshot;
    setCurrentTitle(snap.title);
    const baseData = { ...(vacancy.data || {}), ...latestDataRef.current };
    const merged = mergeIntakeSnapshotIntoVacancy({ data: baseData }, snap);
    if (Array.isArray((merged as any).builtVacancySections)) {
      (merged as any).builtVacancySections = ((merged as any).builtVacancySections as any[]).map((s) => ({ ...s }));
    }
    const patch = pickIntakeKeysFromData(merged);
    setData((prev) => ({
      ...prev,
      ...patch,
      vacancyText: (snap.data as any).vacancyText ?? snap.description,
      finalVacancyContent: snap.description,
      ...(Array.isArray((patch as any).builtVacancySections)
        ? {
              builtVacancySections: ((patch as any).builtVacancySections as any[]).map((s) => ({ ...s })),
          }
        : {}),
    }));

    const descForAudit = buildDescriptionHTML(merged as any);
    lastPersistedAuditRef.current = {
      title: snap.title,
      description: descForAudit,
      data: { ...merged },
      opdrachtgever_id: snap.opdrachtgever_id ?? vacancy.opdrachtgever_id ?? null,
      eindklant_id: snap.eindklant_id ?? vacancy.eindklant_id ?? null,
      shared_with_users: snap.shared_with_users ?? vacancy.shared_with_users,
      shared_with_clients: snap.shared_with_clients ?? vacancy.shared_with_clients,
      status: (snap.status as string | undefined) ?? ((vacancy as any).status as string | undefined) ?? null,
    };
    lastIntakeVersionAnchorRef.current = snap;

    if (onVacancyUpdate) {
      onVacancyUpdate({
        ...vacancy,
        title: snap.title,
        description: snap.description,
        opdrachtgever_id: snap.opdrachtgever_id ?? vacancy.opdrachtgever_id,
        eindklant_id: snap.eindklant_id ?? vacancy.eindklant_id,
        shared_with_users: snap.shared_with_users ?? vacancy.shared_with_users,
        shared_with_clients: snap.shared_with_clients ?? vacancy.shared_with_clients,
        data: merged,
      } as any);
    }
    setShowIntakeHistoryModal(false);
  };

  const resolveFlowActor = async (): Promise<VacancyFlowActor | undefined> => {
    if (actorUser?.id || actorUser?.name) {
      return { ...actorUser, role: String(userRole) };
    }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        return {
          id: user.id,
          name: (user.user_metadata?.full_name as string) || user.email || 'Gebruiker',
          email: user.email || undefined,
          role: String(userRole),
        };
      }
    } catch { /* ignore */ }
    return undefined;
  };

  const handleSupabaseSync = async (
    silent = false,
    overrideData?: any,
    persistOptions?: { vacancyStatus?: string }
  ) => {
    if (!vacancy?.id) return;
    if (isClientPortalHostname()) return;
    if (!silent) setSyncing(true);
    try {
      const persistVacancyStatus =
        isClientPortalHostname() ? undefined : persistOptions?.vacancyStatus;

      const currentData = overrideData || latestDataRef.current;
      const currentStatus = STEPS[currentStepIndex]?.id || VacancyStep.RESEARCH;

      const descriptionHTML = resolveDescriptionForPersist(currentData);

      let mergedData: any = { ...currentData, flowStep: currentStatus };

      const resolvedVacancyStatus =
        persistVacancyStatus !== undefined
          ? persistVacancyStatus
          : ((vacancy as any).status as string | undefined) ?? null;

      const prevSnap = lastPersistedAuditRef.current;
      const nextSnap: VacancySnapshotForDiff = {
        title: currentTitle,
        description: descriptionHTML,
        data: { ...mergedData } as Record<string, unknown>,
        opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
        eindklant_id: vacancy.eindklant_id ?? null,
        shared_with_users: vacancy.shared_with_users,
        shared_with_clients: vacancy.shared_with_clients,
        status: resolvedVacancyStatus,
      };

      if (prevSnap) {
        const summaries = describeVacancySnapshotChanges(prevSnap, nextSnap);
        if (summaries.length > 0) {
          const actor = await resolveFlowActor();
          const stepMeta = STEPS[currentStepIndex];
          mergedData = appendVacancyFlowStepHistory(mergedData as Record<string, unknown>, {
            kind: 'save',
            stepId: String(stepMeta?.id || currentStatus),
            stepLabel: stepMeta?.label || String(currentStatus),
            changeSummaries: summaries,
          }, actor) as any;
        }
      }

      const anchor =
        lastIntakeVersionAnchorRef.current ??
        lastIntakeHistorySnapshot((vacancy.data as Record<string, unknown>) || undefined) ??
        intakeBaselineSnapshotFromVacancy(vacancy as Vacancy);
      const nextIntakeSnap = pickIntakeSnapshotFromUpdates({
        title: currentTitle,
        description: descriptionHTML,
        status: (resolvedVacancyStatus as string | undefined) ?? ((vacancy as any).status as string | undefined),
        clientId: (vacancy as any).clientId ?? (vacancy as any).client_id,
        opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
        eindklant_id: vacancy.eindklant_id ?? null,
        shared_with_users: vacancy.shared_with_users,
        shared_with_clients: vacancy.shared_with_clients,
        data: mergedData as Record<string, unknown>,
      });
      if (!intakeSnapshotsEqual(anchor, nextIntakeSnap)) {
        const actor = await resolveFlowActor();
        const prevForDiff = snapshotToVacancySnapshotForDiff(anchor);
        const nextForDiff = snapshotToVacancySnapshotForDiff(nextIntakeSnap);
        const textLines = intakeSnapshotTextDiffSummaries(anchor, nextIntakeSnap);
        const structural = describeVacancySnapshotChanges(prevForDiff, nextForDiff);
        let summaries = mergeStructuralAndTextDiffLines(textLines, structural);
        if (summaries.length === 0) {
          summaries = ['Opgeslagen via werkstroom (Vacature Builder / sync).'];
        }
        mergedData = appendIntakeVersionHistory(
          mergedData as Record<string, unknown>,
          nextIntakeSnap,
          summaries,
          actor
        ) as any;
      }
      lastIntakeVersionAnchorRef.current = nextIntakeSnap;

      const updatePayload: any = {
        title: currentTitle,
        data: mergedData,
        description: descriptionHTML,
      };
      if (persistVacancyStatus !== undefined) {
        updatePayload.status = persistVacancyStatus;
      }

      await supabase_update(vacancy.id, updatePayload);

      lastPersistedAuditRef.current = {
        title: currentTitle,
        description: descriptionHTML,
        data: { ...mergedData },
        opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
        eindklant_id: vacancy.eindklant_id ?? null,
        shared_with_users: vacancy.shared_with_users,
        shared_with_clients: vacancy.shared_with_clients,
        status: resolvedVacancyStatus,
      };

      // Keep local editor state in sync with what we just persisted (incl. flowStepHistory +
      // intakeVersionHistory). Otherwise the next autosave/silent sync rebuilds `mergedData` from
      // stale `latestDataRef` and overwrites Supabase without those arrays — the UI briefly shows
      // the new history from `onVacancyUpdate`, then it disappears after the following save.
      setData((prev) => ({ ...prev, ...mergedData }));

      const updatedVacancy: any = {
        ...vacancy,
        title: currentTitle,
        data: mergedData,
        description: descriptionHTML,
        ...(persistVacancyStatus !== undefined && { status: persistVacancyStatus }),
      };

      if (onVacancyUpdate) onVacancyUpdate(updatedVacancy);
      setLastSaved(new Date().toLocaleTimeString());
    } catch (e: any) {
      if (!silent) alert(`Fout: ${e.message}`);
    } finally { if (!silent) setSyncing(false); }
  };

  const handleNavigate = async (requestedIndex: number) => {
    let newIndex = requestedIndex;
    if (isClientPortalHostname()) {
      while (newIndex >= 0 && newIndex < STEPS.length && STEPS[newIndex]?.id === VacancyStep.STRATEGY) {
        newIndex += requestedIndex > currentStepIndex ? 1 : -1;
      }
      if (newIndex < 0 || newIndex >= STEPS.length) return;
    }
    if (!canSeeStep(STEPS[newIndex].id)) return;
    const currentId = STEPS[currentStepIndex]?.id;
    const targetId = STEPS[newIndex]?.id;
    if (
      currentId === VacancyStep.INTAKE &&
      targetId !== VacancyStep.INTAKE &&
      newIndex !== currentStepIndex &&
      intakeStepRef.current
    ) {
      try {
        const saved = await intakeStepRef.current.saveIfDirty();
        if (!saved) {
          setSyncing(false);
          return;
        }
      } catch (e) {
        console.error('Intake save before navigate failed', e);
        setSyncing(false);
        return;
      }
    }
    setSyncing(true);
    try {
      const currentData = latestDataRef.current;
      const targetStepId = STEPS[newIndex].id;

      if (isClientPortalHostname()) {
        setCurrentStepIndex(newIndex);
        setSyncing(false);
        return;
      }

      const isTargetIntermediate = ![VacancyStep.INTAKE, VacancyStep.RESEARCH, VacancyStep.COMM_TEMPLATES, VacancyStep.PUT_LIVE, VacancyStep.STRATEGY, VacancyStep.OPTIMIZATION, VacancyStep.SHORTLIST, VacancyStep.INTERVIEW_KANBAN].includes(targetStepId as VacancyStep);
      const isInterceptorActiveForTarget = currentData.werfplaatsConfig && !isStaff && isTargetIntermediate;

      const navDescriptionHTML = resolveDescriptionForPersist(currentData);
      const updatePayload: any = {
        title: currentTitle,
        data: { ...currentData },
        description: navDescriptionHTML
      };

      if (!isInterceptorActiveForTarget) {
        const currentVacancyIndex = STEPS.findIndex(s => s.id === (vacancy.data as any)?.flowStep);
        if (newIndex > currentVacancyIndex || currentVacancyIndex === -1) {
          updatePayload.data.flowStep = targetStepId;
        }
      }

      await supabase_update(vacancy.id, updatePayload);
      setLastSaved(new Date().toLocaleTimeString());

      lastPersistedAuditRef.current = {
        title: currentTitle,
        description: navDescriptionHTML,
        data: { ...(vacancy.data as any), ...updatePayload.data },
        opdrachtgever_id: vacancy.opdrachtgever_id ?? null,
        eindklant_id: vacancy.eindklant_id ?? null,
        shared_with_users: vacancy.shared_with_users,
        shared_with_clients: vacancy.shared_with_clients,
        status: (vacancy as any).status ?? null,
      };

      if (newIndex !== currentStepIndex && updatePayload.data) {
        setData((prev) => ({ ...prev, ...updatePayload.data }));
        if (onVacancyUpdate) {
          onVacancyUpdate({
            ...vacancy,
            title: currentTitle,
            data: { ...vacancy.data, ...updatePayload.data },
            description: navDescriptionHTML,
          });
        }
      }
      
      setCurrentStepIndex(newIndex);
      
      // Automatic builder start removed per user request: "niet automatische weghalen of overschrijven"
    } catch (e) {
      setCurrentStepIndex(newIndex);
    } finally { setSyncing(false); }
  };

  const addLog = (msg: string) => setProcessingLogs(prev => [...prev.slice(-3), msg]);

  // --- REGENERATION HANDLERS ---
  const regenerateResearch = async (extraPrompt?: string) => {
    setIsRegenerating('research');
    setPromptMode(null);
    try {
      let customPrompt;
      try {
        const { supabase_get_client_settings } = await import('../services/supabaseService');
        const settings = await supabase_get_client_settings(vacancy.clientId);
        customPrompt = settings?.ai_prompts_settings?.marketResearch;
      } catch(e) {}
      
      const intakeData = data.finalVacancyContent || 'Onbekend';
      const resText = await generateMarketResearch(vacancy.title, intakeData, extraPrompt, customPrompt);
      const marketInsights = await generateMarketInsights(vacancy.title, intakeData);
      
      const updatedData = { ...data, marketResearch: resText, marketResearchInsights: marketInsights };
      setData(updatedData);
      await handleSupabaseSync(true, updatedData);
    } catch (e) { console.error(e); } finally { setIsRegenerating(null); }
  };

  const regeneratePersona = async (extraPrompt?: string) => {
    setIsRegenerating('persona');
    setPromptMode(null);
    try {
      let customPrompt;
      try {
        const { supabase_get_client_settings } = await import('../services/supabaseService');
        const settings = await supabase_get_client_settings(vacancy.clientId);
        customPrompt = settings?.ai_prompts_settings?.persona;
      } catch(e) {}
      
      const intakeData = data.finalVacancyContent || 'Onbekend';
      const persData = await generatePersona(vacancy.title, data.marketResearch || '', intakeData, extraPrompt, customPrompt);
      
      const updatedData = {
        ...data,
        persona: persData.description, // Fallback/Summary
        personaConfig: persData
      };
      setData(updatedData);
      await handleSupabaseSync(true, updatedData);
    } catch (e) { console.error(e); } finally { setIsRegenerating(null); }
  };

  const regenerateTemplates = async () => {
    setIsRegenerating('templates');
    try {
      // SEO step removed
      // if (currentStep.id === VacancyStep.TONE_SEO) { ... }

      // Verification step removed
      // if (currentStep.id === VacancyStep.AI_VERIFICATION) { ... }

      // Final Vacancy step removed
      // if (currentStep.id === VacancyStep.VACANCY_FINAL) { ... }

      // ... (rest of file)

      // Fix generateStructuredTemplates call in regenerateTemplates
      const structured = await generateStructuredTemplates(vacancy.title, data.persona || '');
    } catch (e) { console.error(e); } finally { setIsRegenerating(null); }
  };

  // --- AUTO MATCHING ---
  const handleAutoMatch = async () => {
    addLog("Talent Pool Matching...");
    try {
      // 1. Fetch Talent Pool for this client
      const pool = await supabase_get_talent_pool(vacancy.clientId);
      if (!pool || pool.length === 0) return;

      let matchesFound = 0;

      // 2. Perform Match
      // We limit to top 5 most recent candidates to save AI tokens for this demo flow
      const candidatesToScan = pool.slice(0, 5);

      for (const profile of candidatesToScan) {
        if (!profile || !profile.candidate) continue;
        const matchResult = await performRealMatch({
          name: profile.candidate.name,
          role: profile.candidate.role,
          summary: profile.parsed_cv?.summary,
          skills: profile.tags?.map((t: any) => t.name) || [],
          timeline: profile.parsed_cv?.timeline
        }, vacancy);

        if (matchResult.score > 65) {
          // 3. Create Application
          await supabase_submit_application(vacancy.id, vacancy.clientId, {
            firstName: profile.candidate.name.split(' ')[0],
            lastName: profile.candidate.name.split(' ').slice(1).join(' '),
            email: profile.candidate.email,
            cvSummary: `Auto-matched from Talent Pool. Score: ${matchResult.score}.`,
            matchScore: matchResult.score,
            parsedData: profile.parsed_cv || profile.ai_parsed_data || {},
            motivation: "Automatisch gematcht door Werftools AI."
          });
          matchesFound++;

          // Auto-create task for recruiter
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase.from('todos').insert({
              title: `Nieuwe kandidaat op vacature: check ${profile.candidate.name}`,
              description: `Kandidaat is automatisch gematcht vanuit de Talent Pool. Match score: ${matchResult.score}%.`,
              status: 'Pending',
              user_id: user.id,
              assignee_id: vacancy.assignedRecruiterId || user.id, // Assign to vacancy recruiter if exists, else self
              client_id: vacancy.clientId,
              vacancy_id: vacancy.id,
              due_date: new Date(Date.now() + 86400000).toISOString() // Due tomorrow
            });
          }
        }
      }
      if (matchesFound > 0) {
        alert(`${matchesFound} kandidaten uit je Talent Pool zijn automatisch toegevoegd aan de pipeline!`);
      }
    } catch (e) {
      console.error("Auto match error", e);
    }
  };

  const handleCancelWerfplaats = async () => {
    if (!confirm("Weet je zeker dat je de Werfplaats service wilt annuleren? Dit stopt de werving.")) return;
    const newData = { ...data, werfplaatsConfig: undefined };
    setData(newData);
    setWerfplaatsConfig(null);
    setStrategyChoice(null);
    await handleSupabaseSync(true, newData);
  };

  const startEngine = async () => {
    if (isStepReadOnly(STEPS[currentStepIndex].id)) return;
    if (userCredits < COST_ESTIMATE) { alert("Credits tekort!"); return; }
    setIsStarted(true);
    setIsAutoProcessing(true);
    setLoading(true);
    setLoading(true);
    try {
      const titleToUse = currentTitle || vacancy.title;
      const intakeData = data.finalVacancyContent || 'Onbekend';
      
      let mrPrompt;
      let persPrompt;
      try {
        const { supabase_get_client_settings } = await import('../services/supabaseService');
        const settings = await supabase_get_client_settings(vacancy.clientId);
        mrPrompt = settings?.ai_prompts_settings?.marketResearch;
        persPrompt = settings?.ai_prompts_settings?.persona;
      } catch(e) {}

      addLog("Markt scannen...");
      const resText = await generateMarketResearch(titleToUse, intakeData, undefined, mrPrompt);
      addLog("Data berekenen...");
      const marketInsights = await generateMarketInsights(titleToUse, intakeData);
      addLog("Persona bouwen...");
      const persData = await generatePersona(titleToUse, resText, intakeData, undefined, persPrompt);
      addLog("Templates weven...");
      const structured = await generateStructuredTemplates(titleToUse, persData.description);
      addLog("Strategie bepalen...");
      // Marketing Strategy generation removed as step is deprecated

      const updatedData: Partial<VacancyData> = {
        ...data,
        marketResearch: resText,
        marketResearchInsights: marketInsights,
        persona: persData.description,
        personaConfig: persData,
        // marketingConfig: marketingStrat, // REMOVED
        // toneAndSeo: seoText, // REMOVED
        linkedinSearchString: structured.linkedinSearchString,
        metaAdsCopy: structured.metaAdsCopy,
        structuredTemplates: {
          jobText: { items: structured.jobText, selectedIndex: 0 },
          linkedinPost: { items: structured.linkedinPost, selectedIndex: 0 },
          inMail: { items: structured.inMail, selectedIndex: 0 },
          whatsappMessage: { items: structured.whatsappMessage, selectedIndex: 0 }
        }
        // verificationReport: verification // REMOVED
      };

      setData(updatedData);
      if (onUpdateCredits) onUpdateCredits(userCredits - COST_ESTIMATE);
      // Sync generated content to description
      await handleSupabaseSync(true, { ...updatedData, flowStep: VacancyStep.RESEARCH });
      setIsAutoProcessing(false);
      setLoading(false);
      setLastSaved(new Date().toLocaleTimeString());

      // Auto-navigate to Research step to show results
      // (We are already on the Research step because shouldAutoStartEngine triggered here, so no need to call handleNavigate again)
    } catch (err: any) {
      console.error(err);
      setIsAutoProcessing(false);
      setLoading(false);
      setEngineError(err.message || "Onbekende fout tijdens generatie");
    }
  };

  useEffect(() => {
    const researchIndex = STEPS.findIndex(s => s.id === VacancyStep.RESEARCH);
    if (shouldAutoStartEngine && currentStepIndex === researchIndex) {
      if (!isAutoProcessing && !data.marketResearch) {
        startEngine();
      }
      setShouldAutoStartEngine(false);
    }
  }, [currentStepIndex, shouldAutoStartEngine]);

  // Removed auto-build logic for Vacancy Builder: User explicitly requested to not auto-run it.
  // The user will now manually click 'Start Builder' or 'Opnieuw Genereren'.

  const generateTemplatesOnly = async () => {
    if (isStepReadOnly(VacancyStep.COMM_TEMPLATES)) return;
    setIsAutoProcessing(true);
    setLoading(true);
    try {
      addLog("Templates genereren...");
      const personaContext = latestDataRef.current.persona || latestDataRef.current.personaConfig?.description || vacancy.description;
      const structured = await generateStructuredTemplates(vacancy.title, personaContext);

      const freshData = {
        ...latestDataRef.current,
        linkedinSearchString: structured.linkedinSearchString,
        metaAdsCopy: structured.metaAdsCopy,
        structuredTemplates: {
          jobText: { items: structured.jobText, selectedIndex: 0 },
          linkedinPost: { items: structured.linkedinPost, selectedIndex: 0 },
          inMail: { items: structured.inMail, selectedIndex: 0 },
          whatsappMessage: { items: structured.whatsappMessage, selectedIndex: 0 },
        },
      };
      setData(freshData);
      await handleSupabaseSync(true, freshData);

      setIsAutoProcessing(false);
      setLoading(false);
    } catch (e: any) {
      console.error(e);
      if (e.message?.includes('429') || e.message?.includes('Exhausted') || e.message?.includes('RESOURCE_EXHAUSTED')) {
         setShowGeminiExhaustedModal(true);
      } else {
         alert("Kon templates niet genereren.");
      }
      setIsAutoProcessing(false);
      setLoading(false);
    }
  };

  const selectTemplateItem = (category: keyof VacancyTemplates, index: number) => {
    if (isStepReadOnly(VacancyStep.COMM_TEMPLATES)) return;
    if (!data.structuredTemplates) return;
    const newTemplates = { ...data.structuredTemplates };
    newTemplates[category].selectedIndex = index;
    setData(prev => ({
      ...prev,
      structuredTemplates: newTemplates,
      finalVacancyContent: category === 'jobText' ? newTemplates.jobText.items[index].content : prev.finalVacancyContent
    }));
  };

  const generateVacancyBuilder = async () => {
    if (isStepReadOnly(VacancyStep.COMM_TEMPLATES)) return;
    setIsAutoProcessing(true);
    setLoading(true);
    try {
      addLog("Vacature opbouw genereren...");
      const titleToUse = currentTitle || vacancy.title;
      const structure = vacancy.client?.vacancyStructure || ['Intro', 'Functieomschrijving', 'Werkzaamheden', 'Functie eisen', 'Overige informatie'];
      const research = data.marketResearch || '';
      const persona = data.personaConfig?.description || data.persona || '';
      const intakeData = data.finalVacancyContent || vacancy.description || '';

      let clientPrompts: Record<string, string> = {};
      try {
        const { supabase_get_client_settings } = await import('../services/supabaseService');
        const settings = await supabase_get_client_settings(vacancy.clientId);
        if (settings?.ai_prompts_settings) clientPrompts = settings.ai_prompts_settings;
      } catch(e) {}

      const generated = await generate_vacancy_from_research(titleToUse, research, persona, intakeData, structure, clientPrompts);

      
      // Combine sections into one final content string
      const combinedHTML = generated.sections.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n');

      const updatedData: Partial<VacancyData> = {
        ...data,
        builtVacancySections: generated.sections,
        finalVacancyContent: combinedHTML
      };

      setData(updatedData);
      setBuilderSection(generated.sections.length > 0 ? 0 : -1);
      await handleSupabaseSync(true, updatedData);
      
      // Also trigger templates in background
      generateTemplatesOnly();
    } catch (e: any) {
      console.error(e);
      if (e.message?.includes('429') || e.message?.includes('Exhausted') || e.message?.includes('RESOURCE_EXHAUSTED')) {
         setShowGeminiExhaustedModal(true);
      } else {
         alert("Kon vacature niet genereren.");
      }
    } finally {
      setIsAutoProcessing(false);
      setLoading(false);
    }
  };

  const handleRefineTemplate = async () => {
    if (isStepReadOnly(VacancyStep.COMM_TEMPLATES) || !refinementPrompt || !data.structuredTemplates) return;
    const currentContent = data.structuredTemplates[templateCategory].items[data.structuredTemplates[templateCategory].selectedIndex].content;
    setIsRefining(true);
    try {
      const refined = await refineTemplateSpecificly(currentContent, refinementPrompt, vacancy.title);
      const newTemplates = { ...data.structuredTemplates };
      newTemplates[templateCategory].items[newTemplates[templateCategory].selectedIndex].content = refined;
      setData({ ...data, structuredTemplates: newTemplates });
      setRefinementPrompt('');
    } catch (e) {
      console.error(e);
    } finally {
      setIsRefining(false);
    }
  };

  const handleRegenerateNetworkShare = async (category: 'linkedinPost' | 'whatsappMessage' | 'inMail') => {
    if (isStepReadOnly(VacancyStep.SHARE_NETWORK) || !data.structuredTemplates) return;
    setIsRegenerating(category);
    try {
      const currentContent = data.structuredTemplates[category].items[data.structuredTemplates[category].selectedIndex].content;
      addLog(`Herschrijven ${category}...`);
      const refined = await refineTemplateSpecificly(currentContent, "Herschrijf dit bericht korter, overtuigender en met een vlotte toon passend bij het platform.", vacancy.title);

      const newTemplates = { ...data.structuredTemplates };
      newTemplates[category].items[newTemplates[category].selectedIndex].content = refined;

      const updatedData = { ...data, structuredTemplates: newTemplates };
      setData(updatedData);
      await handleSupabaseSync(true, updatedData);
    } catch (err) {
      console.error("Failed to regenerate network share snippet", err);
      alert("Er is iets misgegaan tijdens het herschrijven. Probeer opnieuw.");
    } finally {
      setIsRegenerating(null);
    }
  };

  const handlePublishToggle = async () => {
    if (isStepReadOnly(VacancyStep.PUT_LIVE)) return;
    const newStatus = !data.publishedToSite;
    const updates: Partial<VacancyData> = {
      ...data,
      publishedToSite: newStatus,
      publishDate: newStatus ? new Date().toLocaleDateString('nl-NL') : undefined,
      isClosed: newStatus ? false : data.isClosed
    };
    setData(updates as any);

    const persistStatus = newStatus && vacancy.status === 'Concept' ? 'Open' : undefined;
    await handleSupabaseSync(true, updates as any, persistStatus !== undefined ? { vacancyStatus: persistStatus } : undefined);

    if (newStatus) {
      // Trigger Auto Match
      await handleAutoMatch();
    }
  };

  const initABTest = () => {
    if (isStepReadOnly(VacancyStep.LIVE_AB)) return;
    if (data.abTestVariants && data.abTestVariants.length > 0) return;
    const variantA: ABTestVariant = { id: 'var-a', name: 'Variant A (Controle)', content: data.finalVacancyContent || 'Vacature tekst A', views: 0, applications: 0, conversionRate: 0 };
    const variantB: ABTestVariant = { id: 'var-b', name: 'Variant B (Kort & Krachtig)', content: data.structuredTemplates?.jobText.items[1]?.content || 'Kortere variant...', views: 0, applications: 0, conversionRate: 0 };
    setData({ ...data, abTestVariants: [variantA, variantB], abTestStatus: 'draft', activePlatforms: { careerSite: true, linkedin: true, indeed: true, instagram: false } });
  };

  const togglePlatform = (key: keyof typeof data.activePlatforms) => {
    if (isStepReadOnly(VacancyStep.LIVE_AB) || !data.activePlatforms) return;
    setData({ ...data, activePlatforms: { ...data.activePlatforms, [key]: !data.activePlatforms[key] } });
  };

  const startABTest = () => {
    if (isStepReadOnly(VacancyStep.LIVE_AB)) return;
    setData({ ...data, abTestStatus: 'running' });
    const interval = setInterval(() => {
      setData(prev => {
        if (!prev.abTestVariants || prev.abTestStatus !== 'running') {
          clearInterval(interval);
          return prev;
        }
        const updated = prev.abTestVariants.map(v => {
          const newViews = v.views + Math.floor(Math.random() * 5);
          const newApps = v.applications + (Math.random() > 0.8 ? 1 : 0);
          return { ...v, views: newViews, applications: newApps, conversionRate: newViews > 0 ? Math.round((newApps / newViews) * 100) : 0 };
        });
        return { ...prev, abTestVariants: updated };
      });
    }, 3000);
  };

  const stopABTest = () => {
    if (isStepReadOnly(VacancyStep.LIVE_AB)) return;
    setData({ ...data, abTestStatus: 'completed' });
  };

  const currentStep = STEPS[currentStepIndex];
  if (!currentStep) return null; // Safe guard

  const isReadOnlyForRole = isClientPortalHostname() || isStepReadOnly(currentStep.id);
  const isIntermediateStep = ![VacancyStep.INTAKE, VacancyStep.RESEARCH, VacancyStep.COMM_TEMPLATES, VacancyStep.PUT_LIVE, VacancyStep.STRATEGY, VacancyStep.OPTIMIZATION, VacancyStep.SHORTLIST, VacancyStep.INTERVIEW_KANBAN].includes(currentStep.id as VacancyStep);
  const showWerfplaatsInterceptor = data.werfplaatsConfig && !isStaff && isIntermediateStep;

  const renderWerfplaatsActiveState = () => {
    return (
      <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-slate-200/60 text-center animate-fadeIn relative overflow-hidden max-w-2xl mx-auto mt-8">
        <div className="absolute top-0 right-0 w-48 h-48 bg-red-50 rounded-full blur-[60px] -mr-10 -mt-10 pointer-events-none opacity-50"></div>

        <div className="w-16 h-16 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-red-600 shadow-inner border border-red-200 transform rotate-3 relative z-10">
          <Clock size={28} className="animate-pulse" />
        </div>

        <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">Werfplaats is aan de slag!</h2>

        {data.werfplaatsConfig?.selectedTime ? (
          data.werfplaatsConfig.selectedTime === 'Later' ? (
            <div className="bg-amber-50 text-amber-800 p-4 rounded-xl mb-6 border border-amber-200 flex items-center gap-3 justify-center max-w-md mx-auto relative z-10">
              <AlertCircle size={18} className="shrink-0" />
              <p className="text-xs font-bold text-left">Let op: Je hebt nog geen kick-off afspraak ingepland. Doe dit alsnog om de werving te starten.</p>
            </div>
          ) : (
            <div className="bg-red-/50 p-5 rounded-xl mb-6 border border-red- max-w-md mx-auto relative z-10 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Ingeplande Kick-off</p>
              <div className="flex items-center justify-center gap-2 text-red- font-black text-lg mb-3">
                <Calendar size={18} className="text-red-0" />
                {data.werfplaatsConfig.selectedTime}
              </div>
              <div className="inline-flex items-center justify-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-red- text-xs font-bold text-red- shadow-sm w-full">
                <Loader2 size={12} className="animate-spin" /> Wachtend op bevestiging recruiter
              </div>
            </div>
          )
        ) : null}

        <p className="text-slate-500 text-sm max-w-lg mx-auto mb-8 leading-relaxed font-medium relative z-10">
          Onze recruiters zijn druk bezig met het sourcen en screenen van kandidaten. Zodra er een match is, verschijnt deze in je <span className="font-bold text-slate-900">Interview Flow</span>.
        </p>

        <div className="flex justify-center gap-3 flex-wrap relative z-10">
          <button onClick={() => handleNavigate(STEPS.findIndex(s => s.id === VacancyStep.SHORTLIST))} className="px-6 py-3 bg-gradient-to-r from-red- to-indigo-600 text-white rounded-xl font-black shadow-md hover:shadow-lg transition-all flex items-center gap-2 text-xs">
            <Users size={16} /> Naar Interview Flow
          </button>

          <button onClick={() => { setStrategyChoice('werfplaats'); setIsEditingConfig(true); handleNavigate(STEPS.findIndex(s => s.id === VacancyStep.STRATEGY)); }} className="px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-xs hover:text-red- hover:border-red- hover:bg-red-50 transition-all flex items-center gap-2 shadow-sm">
            <Settings size={14} /> Afspraak Wijzigen
          </button>

          <button onClick={handleCancelWerfplaats} className="px-4 py-3 bg-white text-slate-400 rounded-xl font-bold text-xs hover:text-red-600 hover:bg-red-50 transition-all underline decoration-slate-200 underline-offset-4">
            Service Annuleren
          </button>
        </div>
      </div>
    );
  };


  if (showPreview) {
    const builtPreviewHTML = data.builtVacancySections && data.builtVacancySections.length > 0
      ? data.builtVacancySections.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n')
      : (data.finalVacancyContent || vacancy.description || '');
    const previewSnapshotData = { ...vacancy.data, ...data, finalVacancyContent: builtPreviewHTML };
    const currentVacancySnapshot: Vacancy = { ...vacancy, data: previewSnapshotData };
    return <PublicVacancyView vacancyTitle={vacancy.title} clientId={vacancy.clientId} onBack={() => setShowPreview(false)} currentVacancy={currentVacancySnapshot} />;
  }

  // Kickstart screen removed. Flow now auto-starts engine and shows processing state in main view.

  const activeSections = data.builtVacancySections || (
    data.finalVacancyContent
      ? [{ title: 'Vacaturetekst', content: data.finalVacancyContent }]
      : (vacancy.client?.vacancyStructure || ['Over het bedrijf', 'Functieomschrijving', 'Werkzaamheden', 'Functie-eisen', 'Overige informatie']).map(title => ({ title, content: '' }))
  );

  const isWerfplaatsLead = data.claimStatus === 'Assigned' || !!(vacancy as any).assigned_recruiter_id;
  
  const toggleLead = async (assigned: boolean) => {
    const newState: 'Assigned' | 'Open' | 'Pending' = assigned ? 'Assigned' : 'Open';
    const updatedData = { ...data, claimStatus: newState };
    setData(updatedData);
    await handleSupabaseSync(true, updatedData);
  };

  const selectedContactName = contactPersons.find(p => p.id === data.contactPersonId)?.name || 'Geen recruiter gekozen';

  return (
    <div className={
      isEmbedded 
      ? "flex flex-col md:flex-row h-full w-full overflow-hidden bg-[#f8fafc]" 
      : "fixed inset-0 z-[100] bg-slate-50 flex flex-col md:flex-row h-screen w-screen overflow-hidden"
    }>


      {/* SIDEBAR NAVIGATION */}
      <div className={`w-full md:w-56 bg-white border-r border-slate-200 p-4 shrink-0 flex flex-col overflow-y-auto custom-scrollbar z-20 ${isEmbedded ? '' : 'shadow-xl'}`}>
        {!hideHeader && (
          <button
            onClick={handleBack}
            className="mb-4 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors group"
          >
            <ArrowLeft size={12} className="group-hover:-translate-x-1 transition-transform" /> Terug
          </button>
        )}
        <div className="mb-6 flex items-center gap-2 px-1 pt-1">
          <div className="w-6 h-6 bg-slate-900 rounded-md flex items-center justify-center">
            <WerftoolsLogo className="w-3 h-3" color="white" />
          </div>
          <div>
            <h3 className="text-[10px] font-black text-slate-900 uppercase">Workflow</h3>
          </div>
        </div>
        <nav className="space-y-2">
          {DYNAMIC_PHASES.map((phase, pIdx) => {
            const visibleStepsInPhase = phase.range.filter(idx => canSeeStep(STEPS[idx]?.id));
            if (visibleStepsInPhase.length === 0) return null;

            const isCollapsed = collapsedPhases.includes(pIdx);
            const isInterviewMode = STEPS[currentStepIndex]?.id === VacancyStep.SHORTLIST || STEPS[currentStepIndex]?.id === VacancyStep.INTERVIEW_KANBAN;
            const isPhaseActive = phase.range.includes(currentStepIndex);

            return (
              <div key={pIdx}>
                <div
                  className="flex justify-between items-center group mb-1 p-1.5 -ml-1 rounded-lg transition-colors cursor-default"
                >
                  <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 group-hover:text-slate-900 transition-colors">
                    {phase.name}
                  </p>
                </div>

                <div className="space-y-0.5 relative transition-all duration-300 overflow-hidden max-h-[1000px] opacity-100 mb-4">
                  {/* Timeline line */}
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200 rounded-full"></div>

                  {visibleStepsInPhase.map(idx => {
                    const step = STEPS[idx];
                    if (!step) return null; // FIX: Ensure step exists before rendering

                    const isActive = idx === currentStepIndex;
                    const isCompleted = idx < currentStepIndex;

                    return (
                      <div key={step.id}>
                        <div
                          onClick={() => !isAutoProcessing && !loading && handleNavigate(idx)}
                          className={`relative flex items-center gap-2 px-2 py-1.5 rounded-md transition-all cursor-pointer group z-10 ${isActive
                            ? 'bg-white shadow-sm border border-slate-100'
                            : isAutoProcessing || loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50'
                            }`}
                        >
                          <div
                            className={`w-4 h-4 rounded-full flex items-center justify-center transition-all ${isActive ? 'bg-[#e51b23]' : isCompleted ? 'bg-emerald-500' : 'bg-slate-200'}`}
                            style={(step.id === VacancyStep.SHORTLIST || step.id === VacancyStep.INTERVIEW_KANBAN) ? { boxShadow: '0 0 0 2px #BFDBFE', backgroundColor: isActive ? '#e51b23' : '#EFF6FF' } : {}}
                          >
                            {isActive && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                            {isCompleted && <Check size={10} className="text-white" />}
                            {(step.id === VacancyStep.SHORTLIST || step.id === VacancyStep.INTERVIEW_KANBAN) && !isActive && !isCompleted && <div className="w-1.5 h-1.5 bg-red-0 rounded-full" />}
                          </div>
                          <span
                            className={`text-[10px] uppercase tracking-tight truncate`}
                            style={(step.id === VacancyStep.SHORTLIST || step.id === VacancyStep.INTERVIEW_KANBAN) ? { color: '#e51b23', fontWeight: 900 } : { color: isActive ? '#0F172A' : '#64748B', fontWeight: isActive ? 700 : 500 }}
                          >
                            {step.label.split('. ')[1]}
                          </span>
                        </div>
                        {/* Sub-steps for Interview Flow */}
                        {isActive && step.id === VacancyStep.SHORTLIST && (
                          <div className="ml-8 mt-1 space-y-1 border-l border-slate-200 pl-2 animate-fadeIn">
                            {['Nieuw', 'Screening', 'Interview', 'Aanbod', 'Aangenomen', 'Afgewezen'].map((stageName, sIdx) => (
                              <div key={sIdx} className="text-[9px] font-bold text-slate-400 py-0.5 flex items-center gap-2">
                                <div className="w-1 h-1 rounded-full bg-slate-300"></div> {stageName}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 flex flex-col min-w-0 bg-slate-50 relative h-full">

        {/* Engine Error Modal */}
        {engineError && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                <div className="bg-white rounded-[2rem] shadow-2xl p-8 max-w-md w-full relative flex flex-col items-center text-center animate-scaleIn border border-red-100">
                    <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-6 ring-8 ring-red-50/50">
                        <AlertTriangle size={36} />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">AI Engine Gestopt</h2>
                    <p className="text-sm text-slate-500 mb-6 leading-relaxed font-medium">
                        Er ging iets mis tijdens het genereren. Soms is de AI overbelast.
                    </p>
                    <div className="w-full bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs text-slate-600 font-mono text-left mb-8 overflow-hidden break-words">
                        {engineError}
                    </div>
                    <div className="flex gap-3 w-full">
                        <button 
                            onClick={() => setEngineError(null)}
                            className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all"
                        >
                            Sluiten
                        </button>
                        <button 
                            onClick={() => {
                                setEngineError(null);
                                startEngine();
                            }}
                            className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-md shadow-red-600/20"
                        >
                            <RefreshCw size={18} />
                            Opnieuw Proberen
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Gemini Exhausted Modal */}

        <UsageStatsBar
          client={vacancy.client || {} as any}
          usage={{
            searches: 0, // Mock usage set to 0 as requested
            interviews: 0,
            hires: 0
          }}
          onUpgrade={() => alert("Upgrade flow not implemented yet")}
        />
        {!hideHeader && (
        <header className="px-6 py-3 border-b border-slate-100 bg-white/95 backdrop-blur-sm z-30 sticky top-0 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <button onClick={handleBack} disabled={isAutoProcessing} className="p-1.5 bg-slate-50 text-slate-400 rounded-lg hover:bg-slate-100 hover:text-slate-900 transition-all">
              <Home size={16} />
            </button>
            <div>
              <div className="flex flex-col">
                <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                  <Briefcase className="text-[#e51b23] shrink-0" size={24} />
                  {currentTitle}
                </h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Stap {currentStepIndex + 1}: {currentStep.label.split('. ')[1] || currentStep.label}</span>
                  {isReadOnlyForRole && !isStaff && <span title="Alleen Lezen"><Lock size={10} className="text-amber-500" /></span>}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-row items-center gap-4 border-r border-slate-200 pr-4 mr-2 hidden md:flex">
               {isWerfplaatsLead ? (
                 <div className="flex items-center gap-3 bg-red-50 py-1 px-3 rounded-lg border border-red-100">
                    <img src="https://ui-avatars.com/api/?name=Thom+de+Vries&background=0D8ABC&color=fff&size=128" alt="Thom de Vries" className="w-8 h-8 rounded-full border-2 border-white shadow-sm object-cover" />
                    <div className="flex flex-col items-start">
                      <span className="text-[9px] font-black uppercase text-red-500">Jouw Support Contact</span>
                      <span className="text-[11px] font-bold text-red-900">Thom de Vries</span>
                    </div>
                 </div>
               ) : (
                 <div className="flex flex-col items-end">
                   <span className="text-[9px] font-black uppercase text-slate-400">Recruiter</span>
                   <span className="text-[11px] font-bold text-slate-700">{selectedContactName}</span>
                 </div>
               )}
               
               <div className="h-6 w-px bg-slate-200 mx-1"></div>

               <div className="flex flex-col items-end">
                 <span className="text-[9px] font-black uppercase text-slate-400 mb-0.5">In the lead</span>
                 <div className="flex items-center bg-slate-100 p-0.5 rounded-md border border-slate-200">
                   <button onClick={() => toggleLead(false)} className={`px-2 py-0.5 rounded text-[9px] font-black uppercase transition-all ${!isWerfplaatsLead ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>Eigen</button>
                   <button onClick={() => toggleLead(true)} className={`px-2 py-0.5 rounded text-[9px] font-black uppercase transition-all ${isWerfplaatsLead ? 'bg-white shadow-sm text-[#e51b23]' : 'text-slate-400 hover:text-slate-600'}`}>Werfplaats</button>
                 </div>
               </div>
            </div>

            {!isReadOnlyForRole && (
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="flex items-center gap-2 bg-white text-slate-600 border border-slate-200 px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm active:scale-95"
                title="Team / Recruiters toevoegen"
              >
                <Users size={14} /> Team
              </button>
            )}
            {!isReadOnlyForRole && (
              <button
                onClick={async () => {
                  const { data: tmpl } = await supabase.from('vacancy_templates').select('id, name, content, created_at').eq('client_id', vacancy.clientId).order('created_at', { ascending: false });
                  setTemplatePickerList(tmpl || []);
                  setShowTemplatesPickerModal(true);
                }}
                className="flex items-center gap-2 bg-white text-slate-600 border border-slate-200 px-4 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-600 transition-all shadow-sm active:scale-95"
                title="Laad een bestaand sjabloon"
              >
                <LayoutTemplate size={14} /> Sjablonen
              </button>
            )}
            {!isReadOnlyForRole && (
              <button onClick={() => handleSupabaseSync(false)} disabled={syncing} className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#e51b23] transition-all shadow-lg active:scale-95 disabled:opacity-50">
                {syncing ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
              </button>
            )}
            
            <button onClick={() => setShowPreview(true)} className="flex items-center gap-2 bg-white text-slate-600 border border-slate-200 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all">
              <Eye size={14} /> Voorbeeld
            </button>
            <div className="flex items-center gap-2 ml-2 border-l border-slate-200 pl-4">
               <button
                  onClick={() => handleNavigate(currentStepIndex - 1)}
                  disabled={currentStepIndex === 0}
                  className="flex items-center gap-1 px-3 py-2.5 text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} /> Vorige
                </button>
                {currentStep.id === VacancyStep.PUT_LIVE ? (
                  <button
                    disabled={isReadOnlyForRole}
                    onClick={() => {
                        handleSupabaseSync();
                        if (onVacancyUpdate) onVacancyUpdate({ ...vacancy, data, status: 'Closed' } as any);
                    }}
                    className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg hover:shadow-emerald-500/20 active:scale-95 disabled:opacity-50"
                  >
                    Opslaan & Naar Dashboard <ChevronRight size={16} />
                  </button>
                ) : (
                  <>
                    <div id="vacancy-flow-actions-container" className="flex items-center gap-2 empty:hidden"></div>
                    <button
                      onClick={() => handleNavigate(currentStepIndex + 1)}
                      disabled={currentStepIndex >= STEPS.length - 1}
                      className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Volgende <ChevronRight size={16} />
                    </button>
                  </>
                )}
            </div>

          </div>
        </header>
        )}

        <div className="flex-1 p-8 overflow-y-auto bg-[#F8FAFC] custom-scrollbar">
          {hideHeader && !([VacancyStep.COMM_TEMPLATES, VacancyStep.STRATEGY] as string[]).includes(currentStep.id) && (
            <div className="flex items-center gap-2 mb-4 px-1">
              {STEPS.slice(0, Math.min(STEPS.length, 6))
                .map((step, idx) => ({ step, idx }))
                .filter(({ step }) => !(isClientPortalHostname() && step.id === VacancyStep.STRATEGY))
                .filter(({ idx }) => idx <= currentStepIndex + 1)
                .map(({ step, idx }, fragmentIdx) => (
                <React.Fragment key={step.id}>
                  {fragmentIdx > 0 && <ChevronRight size={12} className="text-slate-300 shrink-0" />}
                  <button
                    onClick={() => !isAutoProcessing && handleNavigate(idx)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors ${
                      idx === currentStepIndex
                        ? 'bg-[#e51b23] text-white'
                        : idx < currentStepIndex
                        ? 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                        : 'text-slate-300 cursor-default'
                    }`}
                    disabled={idx > currentStepIndex || isAutoProcessing}
                  >
                    {step.label.split('. ')[1] || step.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}
          {isAutoProcessing ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-red-500 blur-xl opacity-20 rounded-full animate-pulse"></div>
                <Loader2 size={64} className="text-[#e51b23] animate-spin relative z-10" />
              </div>
              <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Froukje is bezig...</h3>
              <div className="mt-6 space-y-2">
                {processingLogs.map((log, i) => (
                  <p key={i} className="text-xs font-bold text-slate-400 uppercase tracking-widest animate-fadeIn">{log}</p>
                ))}
              </div>
            </div>
          ) : showWerfplaatsInterceptor ? (
            <div className="max-w-6xl mx-auto space-y-8 animate-fadeIn pb-20 pt-8">
              {renderWerfplaatsActiveState()}
            </div>
          ) : (
            <div className="max-w-6xl mx-auto space-y-8 animate-fadeIn pb-20">

              {currentStep.id === VacancyStep.INTAKE && (
                <VacancyIntake
                  ref={intakeStepRef}
                  vacancy={{
                    ...vacancy,
                    title: currentTitle,
                    description: data.vacancyText || vacancy.description || '', // Pass original briefing text
                    data: { ...vacancy.data, ...data }
                  }}
                  onUpdate={(updates) => {
                    const mergedData: Record<string, unknown> = {
                      ...vacancy.data,
                      ...data,
                      ...(updates.data || {}),
                    };
                    const nextFlow: Partial<VacancyData> = {
                      ...data,
                      ...(updates.data || {}),
                    };
                    const patchVacancyText = (updates.data as { vacancyText?: string })?.vacancyText;
                    if (patchVacancyText !== undefined) {
                      mergedData.vacancyText = patchVacancyText;
                      nextFlow.vacancyText = patchVacancyText;
                    }
                    if (updates.description) {
                      mergedData.finalVacancyContent = updates.description;
                      nextFlow.finalVacancyContent = updates.description;
                    }
                    if (Array.isArray((updates.data as { builtVacancySections?: unknown })?.builtVacancySections)) {
                      mergedData.builtVacancySections = (updates.data as { builtVacancySections: unknown })
                        .builtVacancySections;
                      nextFlow.builtVacancySections = mergedData.builtVacancySections as VacancyData['builtVacancySections'];
                    }
                    if (updates.title) {
                      setCurrentTitle(updates.title);
                    }
                    latestDataRef.current = nextFlow as VacancyData;
                    setData(nextFlow);
                    if (onVacancyUpdate) {
                      onVacancyUpdate({
                        ...vacancy,
                        ...(updates.title && { title: updates.title }),
                        ...(updates.description && { description: updates.description }),
                        ...(updates.clientId !== undefined && { clientId: updates.clientId }),
                        ...(updates.opdrachtgever_id !== undefined && { opdrachtgever_id: updates.opdrachtgever_id }),
                        ...(updates.eindklant_id !== undefined && { eindklant_id: updates.eindklant_id }),
                        ...(updates.shared_with_users !== undefined && { shared_with_users: updates.shared_with_users }),
                        ...(updates.shared_with_clients !== undefined && { shared_with_clients: updates.shared_with_clients }),
                        ...(updates.status !== undefined && { status: updates.status }),
                        data: mergedData,
                      } as Vacancy);
                    }
                  }}
                  onNext={() => {
                    setShowPathChoiceModal(true);
                  }}
                  isReadOnly={isReadOnlyForRole}
                  userRole={userRole}
                  actorUser={actorUser}
                />
              )}

              {currentStep.id === VacancyStep.COMM_TEMPLATES && (
                <div className="animate-fadeIn">
                  {isAutoProcessing && !data.builtVacancySections ? (
                     <AiProcessingState logs={processingLogs} title="Vacature Bouwen..." />
                  ) : (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                       <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                          <div>
                            <h4 className="font-black text-slate-900 uppercase tracking-widest flex items-center gap-2 text-sm">
                              <LayoutTemplate size={16} className="text-[#e51b23]" /> 
                              Vacature Builder
                            </h4>
                            <p className="text-[11px] text-slate-500 font-medium mt-0.5">Beheer de dynamische opbouw van de vacaturetekst.</p>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 lg:gap-2.5 items-center">
                             {!isClientPortalHostname() && (
                             <button
                               type="button"
                               onClick={() => setShowIntakeHistoryModal(true)}
                               className="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold text-[11px] transition-colors border shadow-sm shrink-0 bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                             >
                               <History size={14} /> Geschiedenis
                               {intakeVersionsChronological.length > 0 && (
                                 <span className="min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-slate-200 text-[10px] font-black flex items-center justify-center">
                                   {intakeVersionsChronological.length}
                                 </span>
                               )}
                             </button>
                             )}
                             {!isClientPortalHostname() && <div className="hidden lg:block w-px h-5 bg-slate-200 mx-0.5 shrink-0" />}
                             <button
                               onClick={() => setShowSplitView(prev => !prev)}
                               disabled={
                                 !intakeBriefingTextFromVacancy({
                                   description: vacancy.description,
                                   data: { ...(vacancy.data as Record<string, unknown>), ...data },
                                 })
                               }
                               className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold text-[11px] transition-colors border shadow-sm shrink-0 ${showSplitView ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'} disabled:opacity-50`}
                             >
                                <Columns size={14} /> {showSplitView ? 'Verberg Origineel' : 'Toon Origineel'}
                             </button>
                             <div className="hidden lg:block w-px h-5 bg-slate-200 mx-0.5 shrink-0" />
                             
                             <button
                               onClick={generateVacancyBuilder}
                               disabled={isAutoProcessing || isReadOnlyForRole}
                               className="flex items-center gap-1.5 px-3 py-1.5 bg-[#e51b23] hover:bg-[#B71C1C] text-white rounded-md transition-all font-semibold text-[11px] shadow-sm shrink-0 active:scale-95 disabled:opacity-50"
                             >
                               <Zap size={14} className={isAutoProcessing ? 'animate-pulse' : ''} />
                               {data.builtVacancySections ? 'Opnieuw Genereren' : 'Start Builder'}
                             </button>
                             {!isReadOnlyForRole && (
                               <button
                                 onClick={async () => {
                                   const { data: tmpl } = await supabase.from('vacancy_templates').select('id, name').eq('client_id', vacancy.clientId).order('created_at', { ascending: false });
                                   setExistingTemplates(tmpl || []);
                                   setSaveTemplateName(vacancy.title || 'Mijn Sjabloon');
                                   setSaveTemplateOverwriteId(null);
                                   setShowSaveTemplateModal(true);
                                 }}
                                 disabled={isAutoProcessing}
                                 title="Sla deze secties op als standaard voor dit bedrijf"
                                 className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-black text-white rounded-md transition-all font-semibold text-[11px] shadow-sm shrink-0 disabled:opacity-50"
                               >
                                 <Save size={14} /> Sla op als Sjabloon
                               </button>
                             )}
                             {!isReadOnlyForRole && (
                               <button
                                 onClick={() => handleSupabaseSync(false)}
                                 disabled={syncing || isAutoProcessing}
                                 title="Sla de huidige aanpassingen in dit bouwblok op"
                                 className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-slate-700 border border-slate-200 rounded-md transition-all font-semibold text-[11px] shadow-sm hover:bg-slate-50 shrink-0 disabled:opacity-50"
                               >
                                 {syncing ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
                               </button>
                             )}
                             <button
                               onClick={() => handleNavigate(currentStepIndex + 1)}
                               disabled={currentStepIndex >= STEPS.length - 1}
                               className="flex items-center gap-1.5 px-3 py-1.5 bg-[#e51b23] hover:bg-[#B71C1C] text-white rounded-md transition-all font-semibold text-[11px] shadow-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                             >
                               Volgende <ChevronRight size={14} />
                             </button>
                          </div>
                       </div>
                        <div className="p-6 bg-slate-50">
                           {/* 50/50 Split or full builder */}
                           <div className={showSplitView ? "flex gap-6 items-stretch" : ""}>
                             {/* LEFT: Original text (split mode only) */}
                             {showSplitView &&
                               intakeBriefingTextFromVacancy({
                                 description: vacancy.description,
                                 data: { ...(vacancy.data as Record<string, unknown>), ...data },
                               }) && (
                               <div className="w-1/2 shrink-0 flex flex-col">
                                 <div className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-2 flex items-center gap-2 px-1">
                                   <span className="w-2 h-2 bg-amber-400 rounded-full inline-block"></span>
                                   Originele Tekst / Briefing
                                 </div>
                                 <div
                                   className="flex-1 p-6 bg-amber-50 border border-amber-200 rounded-xl text-[13px] text-slate-700 leading-relaxed prose prose-sm custom-scrollbar"
                                   dangerouslySetInnerHTML={{
                                     __html: intakeBriefingTextFromVacancy({
                                       description: vacancy.description,
                                       data: { ...(vacancy.data as Record<string, unknown>), ...data },
                                     }),
                                   }}
                                 />
                               </div>
                             )}
                             {/* RIGHT: Builder sections */}
                             <div className={showSplitView ? "w-1/2" : "w-full max-w-4xl mx-auto"}>
                             <div className="space-y-6">
                             {/* Render dynamic sections */}
                             {(() => {
                                const currentSections = data.builtVacancySections || (vacancy.client?.vacancyStructure || ['Intro', 'Functieomschrijving', 'Werkzaamheden', 'Functie-eisen', 'Overige informatie']).map((title: string, i: number) => ({ title, content: i === 0 ? (data.finalVacancyContent || vacancy.description || '') : '' }));
                                
                                return (
                                   <>
                                     {currentSections.map((section: any, idx: number) => (
                                       <div key={idx} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden group">
                                          <div className="bg-slate-50 px-4 py-3 border-b border-slate-100 flex justify-between items-start transition-colors">
                                             <div className="flex items-start gap-2 flex-1">
                                                <div className="w-1.5 h-4 bg-[#e51b23] rounded-full shrink-0 mt-5"></div>
                                                <div className="flex-1 w-full min-w-0">
                                                  <input
    type="text"
    value={section.title}
    readOnly={isReadOnlyForRole}
    onChange={(e) => {
        if (isReadOnlyForRole) return;
        let newSections = [...currentSections];
        newSections[idx] = { ...newSections[idx], title: e.target.value };
        const combinedHTML = newSections.map((s) => `<h3>${s.title}</h3>${s.content}`).join('\n\n');
        const updatedData = { ...data, builtVacancySections: newSections, finalVacancyContent: combinedHTML };
        setData(updatedData);
    }}
    onBlur={() => { if (!isReadOnlyForRole) void handleSupabaseSync(true); }}
    className="w-full bg-transparent border-none text-[13px] font-black text-slate-800 uppercase tracking-widest focus:ring-0 p-0 outline-none h-8 disabled:opacity-70"
    placeholder="Sectie Titel (bijv. INTRO)"
/>
                                                </div>
                                             </div>
                                             {!isReadOnlyForRole && (
                                             <button 
                                               type="button"
                                               onClick={() => {
                                                  let newSections = currentSections.filter((_: any, i: number) => i !== idx);
                                                  const combinedHTML = newSections.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n');
                                                  const updatedData = { ...data, builtVacancySections: newSections, finalVacancyContent: combinedHTML };
                                                  setData(updatedData);
                                                  void handleSupabaseSync(true, updatedData);
                                               }}
                                               className="p-1.5 text-slate-400 hover:text-red-500 rounded-md transition-colors opacity-0 group-hover:opacity-100 mt-3 ml-2"
                                               title="Verwijder Sectie"
                                             >
                                               <Trash2 size={14} />
                                             </button>
                                             )}
                                          </div>
                                          <div className="p-2 min-h-[150px]">
                                            {(() => {
                                                const sectionTitle = (section.title || '').toLowerCase();
                                                let defaultPrompt = '';
                                                const prompts = clientSettings?.ai_prompts_settings || {};
                                                if (sectionTitle.includes('intro') || sectionTitle.includes('over de rol') || sectionTitle === 'samenvatting') {
                                                    defaultPrompt = prompts.vacatureIntro || '';
                                                } else if (sectionTitle.includes('omschrijving')) {
                                                    defaultPrompt = prompts.vacatureFunctieomschrijving || prompts.vacatureIntro || '';
                                                } else if (sectionTitle.includes('werkzaam') || sectionTitle.includes('taken') || sectionTitle.includes('verantwoordelijkheden')) {
                                                    defaultPrompt = prompts.vacatureWerkzaamheden || '';
                                                } else if (sectionTitle.includes('eisen') || sectionTitle.includes('vragen') || sectionTitle.includes('profiel')) {
                                                    defaultPrompt = prompts.vacatureEisen || '';
                                                } else if (sectionTitle.includes('voordeel') || sectionTitle.includes('bieden') || sectionTitle.includes('aanbod') || sectionTitle.includes('arbeidsvoorwaard') || sectionTitle.includes('salaris')) {
                                                    defaultPrompt = prompts.vacatureVoordelen || '';
                                                } else if (sectionTitle.includes('overig') || sectionTitle.includes('informatie') || sectionTitle.includes('solliciteer') || sectionTitle.includes('contact')) {
                                                    defaultPrompt = prompts.vacatureOverige || '';
                                                }

                                                return (
                                                    <AiRichTextEditor
                                                        value={section.content}
                                                        readOnly={isReadOnlyForRole}
                                                        onChange={(val) => {
                                                          if (isReadOnlyForRole) return;
                                                          let newSections = [...currentSections];
                                                          newSections[idx] = { ...newSections[idx], content: val };
                                                          const combinedHTML = newSections.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n');
                                                          const updatedData = { ...data, builtVacancySections: newSections, finalVacancyContent: combinedHTML };
                                                          setData(updatedData);
                                                        }}
                                                        minHeight="150px"
                                                        placeholder={`Froukje is klaar om de ${section.title.toLowerCase()} te schrijven...`}
                                                        className="border-none shadow-none ring-0 focus-within:ring-0"
                                                        aiMode="replace"
                                                        onBlur={() => { if (!isReadOnlyForRole) void handleSupabaseSync(true); }}
                                                        standardSections={standardSections}
                                                        defaultAiPrompt={defaultPrompt}
                                                    />
                                                );
                                            })()}
                                          </div>
                                       </div>
                                     ))}
                                     {!isReadOnlyForRole && (
                                     <button 
                                        type="button"
                                        onClick={() => {
                                            const newSections = [...currentSections, { title: 'Nieuwe Sectie', content: '' }];
                                            const combinedHTML = newSections.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n');
                                            const updatedData = { ...data, builtVacancySections: newSections, finalVacancyContent: combinedHTML };
                                            setData(updatedData);
                                            void handleSupabaseSync(true, updatedData);
                                        }}
                                        className="w-full py-4 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-slate-400 hover:bg-slate-100 font-bold text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                                     >
                                        <Plus size={16} /> Sectie Toevoegen
                                     </button>
                                     )}
                                   </>
                                );
                             })()}
                           </div>
                             </div>
                           </div>
                        </div>
                    </div>
                  )}
                </div>
              )}



              {currentStep.id === VacancyStep.SOURCING && (
                <VacancySourcing
                  vacancy={vacancy}
                  onUpdate={(updates) => setData(prev => ({ ...prev, ...updates }))}
                />
              )}
              {currentStep.id === VacancyStep.RESEARCH && (
                <div className="space-y-8">
                  {/* Top Header & Actions */}
                  <div className="flex justify-between items-center bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
                    <div>
                      <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Onderzoek & Persona</h3>
                      <p className="text-sm text-slate-500 font-medium mt-1">Marktanalyse en ideale kandidaat profiel</p>
                    </div>
                    {(data.marketResearch || data.personaConfig) && !promptMode && (
                      <div className="flex gap-2">
                        <RegenerateButton onClick={() => setPromptMode('research')} isLoading={isRegenerating === 'research'} label="Herstart Onderzoek" />
                        <RegenerateButton onClick={() => setPromptMode('persona')} isLoading={isRegenerating === 'persona'} label="Herstart Persona" />
                        <button
                           onClick={() => handleNavigate(currentStepIndex + 1)}
                           disabled={currentStepIndex >= STEPS.length - 1}
                           className="flex items-center gap-2 px-4 py-2.5 bg-[#e51b23] hover:bg-[#B71C1C] text-white rounded-lg transition-all font-black text-[10px] uppercase tracking-widest shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ml-2"
                         >
                           Volgende <ChevronRight size={14} />
                         </button>
                      </div>
                    )}
                  </div>

                  {/* AI Prompt Injection Modal/Area */}
                  {promptMode && (
                    <div className="bg-red-50 p-4 rounded-xl border border-red-100 animate-fadeIn mb-4">
                      <div className="flex items-center gap-2 mb-2">
                         <Sparkles size={16} className="text-[#e51b23]"/>
                         <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Extra instructie voor Froukje</h4>
                      </div>
                      <input 
                         type="text"
                         autoFocus
                         className="w-full text-sm p-3 border border-red-200 rounded-lg outline-none focus:ring-2 focus:ring-red-500 mb-3 bg-white"
                         placeholder="Bijv: Leg meer focus op remote werken en salaris..."
                         value={aiPromptText}
                         onChange={(e) => setAiPromptText(e.target.value)}
                         onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              promptMode === 'research' ? regenerateResearch(aiPromptText) : regeneratePersona(aiPromptText);
                           }
                         }}
                      />
                      <div className="flex gap-2 justify-end">
                         <button onClick={() => setPromptMode(null)} className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors uppercase">Annuleren</button>
                         <button 
                            onClick={() => promptMode === 'research' ? regenerateResearch(aiPromptText) : regeneratePersona(aiPromptText)}
                            className="px-4 py-2 bg-[#e51b23] text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-black transition-colors"
                         >
                            Herstart met Instructie
                         </button>
                      </div>
                    </div>
                  )}

                  {(!data.marketResearch && !data.personaConfig && !isAutoProcessing && !isRegenerating) ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[2rem] border border-slate-200 shadow-sm text-center animate-fadeIn">
                      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6 text-slate-400">
                        <BrainCircuit size={40} />
                      </div>
                      <h3 className="text-xl font-black text-slate-900 mb-2">Analyseer & Bouw Persona</h3>
                      <p className="text-slate-500 font-medium max-w-md mb-8 leading-relaxed">
                        Laat Froukje de originele vacaturetekst analyseren, marktonderzoek doen en een gedetailleerd kandidaatprofiel samenstellen.
                      </p>
                      <button
                        onClick={startEngine}
                        className="bg-[#e51b23] text-white px-8 py-4 rounded-xl font-bold hover:bg-red-700 transition-all flex items-center gap-3 shadow-lg hover:scale-105 active:scale-95 group"
                      >
                        <Zap size={20} className="group-hover:text-yellow-300 transition-colors" />
                        Start AI Engine
                      </button>
                    </div>
                  ) : (isAutoProcessing && (!data.marketResearch || !data.personaConfig)) ? (
                    <AiProcessingState logs={processingLogs} title="Onderzoek & Persona Genereren..." />
                  ) : (
                    <div className="space-y-8 animate-fadeIn">
                      <MarketResearchOverview
                        aiInsights={data.marketResearch}
                        customInsights={data.marketResearchInsights}
                        isLoading={isAutoProcessing || isRegenerating === 'research'}
                      />

                      {data.personaConfig && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
                          {(isAutoProcessing || isRegenerating === 'persona') && (
                            <div className="absolute inset-0 z-10 bg-white/50 backdrop-blur-sm rounded-[2rem] flex items-center justify-center">
                              <Loader2 className="animate-spin text-slate-900" size={32} />
                            </div>
                          )}
                          <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm space-y-6">
                            <div>
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Beschrijvend Profiel</h4>
                              <p className="text-sm font-medium text-slate-700 leading-relaxed whitespace-pre-wrap">{data.personaConfig.description}</p>
                            </div>
                            <div>
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Persoonlijkheidstype</h4>
                              <div className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-bold border border-purple-100">
                                {data.personaConfig.personalityType}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Tone of Voice</h4>
                              <div className="inline-flex items-center px-3 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-bold border border-red-100">
                                {data.personaConfig.toneOfVoice}
                              </div>
                            </div>
                            
                            {data.personaConfig.jobTitleSynonyms && data.personaConfig.jobTitleSynonyms.length > 0 && (
                            <div>
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Alternatieve Functietitels (Synoniemen)</h4>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {data.personaConfig.jobTitleSynonyms.map((syn: string, i: number) => (
                                  <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold border border-indigo-100">{syn}</span>
                                ))}
                              </div>
                            </div>
                            )}

                            {data.personaConfig.steppingStoneRoles && data.personaConfig.steppingStoneRoles.length > 0 && (
                            <div>
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Opstap Functies (Sourcing)</h4>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {data.personaConfig.steppingStoneRoles.map((role: string, i: number) => (
                                  <span key={i} className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-md text-[10px] font-bold border border-amber-100">{role}</span>
                                ))}
                              </div>
                            </div>
                            )}
                          </div>

                          <div className="space-y-6">
                            <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Competenties</h4>
                              <div className="flex flex-wrap gap-2">
                                {data.personaConfig.competencies?.map((comp: string, i: number) => (
                                  <span key={i} className="px-3 py-1.5 bg-slate-50 text-slate-600 rounded-lg text-xs font-bold border border-slate-100">{comp}</span>
                                ))}
                              </div>
                            </div>

                            <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Pullfactoren (Drijfveren)</h4>
                              <ul className="space-y-3">
                                {data.personaConfig.pullFactors?.map((factor: string, i: number) => (
                                  <li key={i} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0"></span>
                                    {factor}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="bg-slate-900 p-8 rounded-[2rem] shadow-lg text-white">
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Aanbod & Voorwaarden</h4>
                              <div className="space-y-4">
                                <div>
                                  <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Marktcomform Salaris</p>
                                  <p className="text-lg font-black tracking-tight">{data.personaConfig.salaryRange}</p>
                                </div>
                                {data.personaConfig.secondaryBenefits && (
                                  <div>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Secundair</p>
                                    <p className="text-sm text-slate-300">{data.personaConfig.secondaryBenefits}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {currentStep.id === VacancyStep.STRATEGY && (
                 <VacancyTeamChoice
                    vacancy={{ ...vacancy, data: { ...vacancy.data, ...data } }}
                    actorUser={actorUser}
                    userRole={userRole}
                    onUpdate={(updates) => {
                       if (updates.data) {
                          setData(prev => ({ ...prev, ...updates.data }));
                       }
                       if (onVacancyUpdate) {
                          onVacancyUpdate({
                            ...vacancy,
                            ...(updates.shared_with_users !== undefined && { shared_with_users: updates.shared_with_users }),
                            ...(updates.shared_with_clients !== undefined && { shared_with_clients: updates.shared_with_clients }),
                            data: {
                              ...vacancy.data,
                              ...data,
                              ...(updates.data || {}),
                            },
                          } as any);
                       }
                    }}
                    onNext={() => handleNavigate(currentStepIndex + 1)}
                    isReadOnly={isReadOnlyForRole}
                 />
              )}


              {currentStep.id === VacancyStep.PUT_LIVE && (
                <div className="h-full flex flex-col gap-6 animate-fadeIn pb-20">
                  {/* Top Header: Settings */}
                  <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-6 relative flex flex-col lg:flex-row items-center justify-between gap-6 shrink-0 z-20">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#e51b23] to-orange-500 rounded-t-[2rem]"></div>
                    <div className="flex-1">
                      <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-1">Werken-bij Site Integratie</h2>
                      <p className="text-slate-500 text-xs font-medium leading-relaxed max-w-md">
                        Controleer de live preview en selecteer een hoofdcontactpersoon voordat je publiceert.
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4">
                      <div className="flex flex-col">
                        <label className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-1 flex items-center justify-between gap-2">
                          Hoofdcontactpersoon
                          {!data.contactPersonId && <span className="text-red-500">* Verplicht</span>}
                        </label>
                        <select
                          value={data.contactPersonId || ''}
                          onChange={async (e) => {
                            const val = e.target.value;
                            const newData = { ...data, contactPersonId: val };
                            setData(newData);
                            // Use newData directly (which already contains full current state)
                            // to avoid overwriting with potentially stale vacancy.data prop
                            await handleSupabaseSync(true, newData);
                          }}
                          className={`w-64 border rounded-xl p-2.5 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none bg-slate-50 transition-colors ${!data.contactPersonId ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}
                          disabled={isReadOnlyForRole}
                        >
                          <option value="">Selecteer een recruiter...</option>
                          {contactPersons.map(p => (
                            <option key={p.id} value={p.id}>{p.name} ({p.email})</option>
                          ))}
                        </select>
                      </div>
                      
                      {!isReadOnlyForRole && (
                        <div className="pt-5">
                          <button
                            onClick={() => {
                              if (!data.publishedToSite && !data.contactPersonId) {
                                alert('Selecteer eerst een hoofdcontactpersoon voordat je de vacature publiceert.');
                                return;
                              }
                              handlePublishToggle();
                            }}
                            className={`flex justify-center items-center gap-2 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-sm active:scale-95 ${data.publishedToSite ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100' : 'bg-slate-900 text-white hover:bg-black'}`}
                          >
                            {data.publishedToSite ? <CheckCircle size={16} /> : <Globe size={16} />}
                            {data.publishedToSite ? 'Vacature staat online' : 'Nu Publiceren op Site'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Main Area: Full Screen Live Preview */}
                  <div className="flex-1 bg-slate-100 rounded-[2rem] border border-slate-200 overflow-hidden relative shadow-inner z-10 hidden lg:flex min-h-[600px]">
                    <div className="absolute top-4 left-4 z-20 pointer-events-none">
                      <div className="bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-lg border border-slate-200/50 text-[10px] font-black uppercase tracking-widest text-slate-500 shadow-sm flex items-center gap-2">
                        <Eye size={12}/> Live Preview
                      </div>
                    </div>
                    {/* Render PublicVacancyView in inline mode */}
                    <div className="w-full h-full overflow-y-auto custom-scrollbar pb-10 pt-16 px-4 md:px-8 bg-slate-50">
                       <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden min-h-full max-w-5xl mx-auto">
                          {(() => {
                            // Always derive finalVacancyContent from builtVacancySections so the
                            // preview shows the styled builder output (with <h3> headers) rather
                            // than the raw intake text that may be stored in finalVacancyContent.
                            const builtHTML = data.builtVacancySections && data.builtVacancySections.length > 0
                              ? data.builtVacancySections.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n')
                              : (data.finalVacancyContent || vacancy.description || '');
                            const previewData = {
                              ...vacancy.data,
                              ...data,
                              finalVacancyContent: builtHTML,
                            };
                            return (
                              <PublicVacancyView 
                                 vacancyTitle={vacancy.title} 
                                 clientId={vacancy.clientId} 
                                 onBack={() => {}} 
                                 currentVacancy={{ ...vacancy, description: builtHTML, data: previewData }} 
                                 isEmbedded={true}
                              />
                            );
                          })()}
                       </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- NEW MARKETING STEPS --- */}
              {currentStep.id === VacancyStep.PREMIUM_JOB_BOARDS && (
              <VacancyPremiumJobboards
                  vacancy={{
                    ...vacancy,
                    data: { ...vacancy.data, ...data }
                  }}
                  onUpdate={async (updates) => {
                    const newData = { ...data, ...updates };
                    setData(newData);
                    await handleSupabaseSync(true, newData);
                  }}
                  isReadOnly={isClientPortalHostname() ? false : isReadOnlyForRole}
                  onNext={() => handleNavigate(currentStepIndex + 1)}
                />
              )}

              {currentStep.id === VacancyStep.SHARE_NETWORK && (
                <VacancyNetworkShare
                  vacancy={{
                    ...vacancy,
                    data: { ...vacancy.data, ...data }
                  }}
                  isRegenerating={!!isRegenerating}
                  onRegenerate={handleRegenerateNetworkShare}
                />
              )}

              {currentStep.id === VacancyStep.SHARE_WEBSITE && (
                <div className="bg-white p-20 rounded-[2.5rem] border border-slate-200 shadow-sm text-center">
                  <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-8 text-indigo-500 shadow-inner"><LayoutTemplate size={40} /></div>
                  <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-4">Deel op Werken-bij site</h3>
                  <p className="text-slate-500 font-medium max-w-md mx-auto">Zet de vacature direct live op je eigen responsieve carriÃ¨rewebsite gemaakt met Werftools.</p>
                  <button className="mt-8 px-8 py-4 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all shadow-lg">Publiceer op Site</button>
                </div>
              )}

              {currentStep.id === VacancyStep.SCREEN_TALENT_POOL && (
                <VacancyCandidates
                  vacancyId={vacancy.id}
                  userRole={userRole}
                  clientId={clientId}
                />
              )}

              {currentStep.id === VacancyStep.OPTIMIZATION && (
                <div className="flex flex-col gap-6 animate-fadeIn h-full">
                  <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-black text-slate-900 flex items-center gap-2 tracking-tight">
                        <BarChart className="text-[#e51b23]" size={20} /> Evaluatie & Optimalisatie
                      </h3>
                      <p className="text-slate-500 text-sm font-medium mt-1">
                        Bekijk de prestaties van je vacature en krijg tips om meer sollicitanten te trekken.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1">
                     <div className="bg-amber-50 rounded-2xl p-8 border border-amber-100 flex flex-col justify-center text-center items-center shadow-inner">
                         <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-6 text-amber-600 shadow-sm border border-amber-200">
                           <TrendingUp size={40} />
                         </div>
                         <h4 className="text-2xl font-black text-slate-900 mb-3 tracking-tiight">Weinig aanwas?</h4>
                         <p className="text-amber-800 font-medium text-sm mb-8 max-w-sm leading-relaxed">
                            Valt het aantal reacties tegen? Laat Froukje de vacaturetekst herevalueren of schakel direct professionele hulp in via Werfplaats om je bereik te vergroten (bijv. extern uitzetten, premium boards, of actieve sourcing).
                         </p>
                         <button 
                           onClick={() => setShowCallWerfplaats(true)}
                           className="w-full max-w-xs bg-amber-500 hover:bg-amber-600 text-white py-4 rounded-xl font-black text-[11px] uppercase tracking-widest shadow-md transition-all hover:-translate-y-1"
                         >
                            Call Werfplaats
                         </button>
                     </div>

                     <div className="bg-white rounded-2xl p-0 border border-slate-200 overflow-hidden relative shadow-sm flex flex-col items-center justify-center">
                        <div className="absolute inset-0 bg-slate-50/50 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center p-8 text-center">
                            <BarChart2 size={48} className="text-slate-300 mb-4" />
                            <h4 className="text-xl font-black text-slate-900 mb-2">Analytics Dashboard</h4>
                            <p className="text-slate-500 text-sm mb-6 max-w-xs">
                              Open het dashboard om de conversie en doorlooptijd (Time-to-Hire) van de vacature in te zien.
                            </p>
                            <button 
                              onClick={async () => {
                                await handleNavigate(STEPS.findIndex(s => s.id === VacancyStep.SHORTLIST));
                                setShowAnalytics(true);
                              }} 
                              className="px-8 py-4 bg-slate-900 text-white font-black rounded-xl shadow-xl hover:bg-black uppercase tracking-widest text-[10px] flex items-center gap-2 hover:scale-105 transition-all"
                            >
                              <Maximize2 size={16} /> Open Analytics
                            </button>
                        </div>
                     </div>
                  </div>
                </div>
              )}

              {currentStep.id === VacancyStep.SHORTLIST && (
                showStageConfig ? (
                  <StageConfigurator vacancyId={vacancy.id} onUpdate={() => setShowStageConfig(false)} />
                ) : showAnalytics ? (
                  <VacancyAnalytics vacancyId={vacancy.id} onClose={() => setShowAnalytics(false)} />
                ) : (
                  <div className="relative h-full flex flex-col">
                    <KanbanBoard
                      vacancyId={vacancy.id}
                      userRole={userRole}
                      isReviewerOnly={isReviewer}
                      onAnalytics={() => setShowAnalytics(true)}
                      onConfigFlow={() => setShowStageConfig(true)}
                    />
                  </div>
                )
              )}

              {currentStep.id === VacancyStep.INTERVIEW_KANBAN && (
                <div className="relative h-full flex flex-col">
                  <InterviewFlow vacancyId={vacancy.id} />
                </div>
              )}

              {![
                VacancyStep.INTAKE,
                VacancyStep.RESEARCH,
                VacancyStep.COMM_TEMPLATES,
                VacancyStep.PUT_LIVE,
                VacancyStep.STRATEGY,
                VacancyStep.SOURCING,
                VacancyStep.PREMIUM_JOB_BOARDS,
                VacancyStep.SHARE_NETWORK,
                VacancyStep.SCREEN_TALENT_POOL,
                VacancyStep.OPTIMIZATION,
                VacancyStep.SHORTLIST,
                VacancyStep.INTERVIEW_KANBAN
              ].includes(currentStep.id) && (
                  <div className="flex flex-col items-center justify-center min-h-[500px] bg-white rounded-[2.5rem] border border-slate-200 shadow-xl text-center p-12 animate-fadeIn relative overflow-hidden">
                    <div className="absolute inset-0 bg-slate-50/50 -z-10"></div>
                    <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mb-8 text-slate-300 shadow-lg border border-slate-100 relative group">
                      <div className="absolute inset-0 bg-slate-50 rounded-full scale-110 opacity-0 group-hover:opacity-100 transition-all duration-700"></div>
                      <div className="relative z-10 scale-150 transform transition-transform duration-700 group-hover:rotate-12 group-hover:scale-125 text-slate-400 group-hover:text-slate-600">
                        {currentStep.icon}
                      </div>
                    </div>
                    <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tight mb-4 max-w-lg leading-none">
                      {currentStep.label.split('. ')[1] || currentStep.label}
                    </h3>
                    <div className="w-16 h-1 bg-[#e51b23] rounded-full mb-6 mx-auto"></div>
                    <p className="text-slate-500 font-medium max-w-md mx-auto mb-10 text-lg leading-relaxed">
                      Deze functionaliteit is onderdeel van een toekomstige update of wordt momenteel handmatig afgehandeld buiten Werftools om.
                    </p>
                    <div className="flex gap-4 justify-center">
                      <button disabled className="px-8 py-4 bg-slate-100 text-slate-400 rounded-xl font-bold text-xs uppercase tracking-widest cursor-not-allowed flex items-center gap-2">
                        <Lock size={14} /> Binnenkort Beschikbaar
                      </button>
                    </div>
                  </div>
                )}
            </div>
          )}
          </div>
      </div>

      {/* Todo Widget - Always available */}
      {
        userId && (
          <TodoWidget
            userId={userId}
            vacancyId={vacancy.id}
            isOpen={showTodos}
            onToggle={() => setShowTodos(!showTodos)}
          />
        )
      }
      {/* Path Choice Modal â€” after intake */}
      {showPathChoiceModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-[200] flex items-center justify-center backdrop-blur-sm animate-fadeIn p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-8 animate-scaleIn">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles size={22} className="text-indigo-600" />
              </div>
              <h2 className="text-xl font-black text-slate-900 mb-2">Hoe verder met deze vacature?</h2>
              <p className="text-[13px] text-slate-500 font-medium">Kies de route die het beste bij dit moment past.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Option A: Onderzoek & Persona */}
              <button
                onClick={() => {
                  setShowPathChoiceModal(false);
                  setShouldAutoStartEngine(true);
                  handleNavigate(STEPS.findIndex(s => s.id === VacancyStep.RESEARCH));
                }}
                className="group flex flex-col items-start gap-3 p-5 bg-indigo-50 border-2 border-indigo-200 hover:border-indigo-500 rounded-xl transition-all"
              >
                <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center text-white shrink-0">
                  <BrainCircuit size={18} />
                </div>
                <div className="text-left">
                  <p className="text-[13px] font-black text-indigo-900">Onderzoek & Persona</p>
                  <p className="text-[11px] text-indigo-600 font-medium mt-0.5">Froukje doet eerst marktonderzoek en bouwt een kandidaatprofiel. Aanbevolen voor nieuwe rollen.</p>
                </div>
              </button>

              {/* Option B: Direct Builder */}
              <button
                onClick={() => {
                  setShowPathChoiceModal(false);
                  handleNavigate(STEPS.findIndex(s => s.id === VacancyStep.COMM_TEMPLATES));
                }}
                className="group flex flex-col items-start gap-3 p-5 bg-emerald-50 border-2 border-emerald-200 hover:border-emerald-500 rounded-xl transition-all"
              >
                <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center text-white shrink-0">
                  <LayoutTemplate size={18} />
                </div>
                <div className="text-left">
                  <p className="text-[13px] font-black text-emerald-900">Direct Vacature Maken</p>
                  <p className="text-[11px] text-emerald-600 font-medium mt-0.5">Sla onderzoek over en ga direct naar de vacaturebouwer. Handig als je al weet wat je wilt.</p>
                </div>
              </button>
            </div>
            <button
              onClick={() => setShowPathChoiceModal(false)}
              className="w-full mt-4 text-[11px] font-medium text-slate-400 hover:text-slate-600 transition-colors"
            >
              Annuleren â€” blijf op intake
            </button>
          </div>
        </div>
      )}

      <IntakeVersionHistoryModal
        open={showIntakeHistoryModal}
        onClose={() => setShowIntakeHistoryModal(false)}
        versionsNewestFirst={intakeVersionsNewestFirst}
        pendingDiffSnap={builderPendingDiffSnap}
        isReadOnly={isReadOnlyForRole}
        emptyHint="Nog geen intake-checkpoints. Ga naar stap 1 Intake en klik Opslaan — daarna verschijnen ze hier (badge) en kun je ze vanaf de Vacature Builder bekijken."
        onRestoreInForm={isReadOnlyForRole ? undefined : restoreIntakeFromHistoryInFlow}
      />

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        itemId={vacancy.id}
        itemType="vacancies"
        currentVisibility={(vacancy as any).visibility || 'private'}
        currentSharedUsers={(vacancy as any).shared_with_users || []}
        currentSharedClients={vacancy.shared_with_clients || []}
        onSave={(visibility, users, clients) => {
          // Vacancy object will be refreshed by parent/timer
          console.log("Team sharing saved: ", visibility, users, clients);
        }}
        clientId={vacancy.clientId}
      />

      {showPreview && (
        <div className="fixed inset-0 bg-slate-900/50 z-[100] flex items-center justify-center backdrop-blur-sm p-4 md:p-8 animate-fadeIn">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-7xl h-full max-h-[90vh] flex flex-col overflow-hidden animate-scaleIn">
            <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-black text-slate-900 uppercase tracking-widest text-sm flex items-center gap-2">
                <Globe size={16} className="text-[#e51b23]" /> Live Site Preview
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
              >
                Ã—
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-slate-50 relative">
              <PublicVacancyView activePageId="vacancies" currentVacancy={vacancy} clientId={vacancy.clientId} />
            </div>
          </div>
        </div>
      )}

      {/* OPSLAAN ALS SJABLOON MODAL */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-end bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowSaveTemplateModal(false)}>
          <div className="bg-white w-full sm:w-[480px] h-full sm:h-auto sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right-8 duration-300" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Opslaan als Sjabloon</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Bewaar de secties als herbruikbaar sjabloon</p>
              </div>
              <button onClick={() => setShowSaveTemplateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {/* Nieuw sjabloon */}
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Nieuw sjabloon opslaan als</label>
                <input
                  type="text"
                  value={saveTemplateName}
                  onChange={e => { setSaveTemplateName(e.target.value); setSaveTemplateOverwriteId(null); }}
                  placeholder="Bijv. Standaard IT-vacature"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] bg-slate-50 transition-all"
                  autoFocus
                />
              </div>

              {/* Bestaande sjablonen overschrijven */}
              {existingTemplates.length > 0 && (
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Of overschrijf een bestaand sjabloon</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                    {existingTemplates.map((t: any) => (
                      <button
                        key={t.id}
                        onClick={() => { setSaveTemplateOverwriteId(t.id); setSaveTemplateName(t.name); }}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left text-sm font-bold transition-all ${saveTemplateOverwriteId === t.id ? 'border-[#e51b23] bg-red-50 text-[#e51b23]' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'}`}
                      >
                        <span>{t.name}</span>
                        {saveTemplateOverwriteId === t.id && <Check size={16} />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3 bg-slate-50">
              <button onClick={() => setShowSaveTemplateModal(false)} className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-black text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-colors">
                Annuleren
              </button>
              <button
                disabled={!saveTemplateName.trim() || isSavingTemplate}
                onClick={async () => {
                  if (!saveTemplateName.trim()) return;
                  setIsSavingTemplate(true);
                  try {
                    const sections = data.builtVacancySections || (vacancy.client?.vacancyStructure || ['Intro', 'Functieomschrijving', 'Werkzaamheden', 'Functie-eisen', 'Overige informatie']).map((title: string) => ({ title, content: '' }));
                    if (saveTemplateOverwriteId) {
                      await supabase.from('vacancy_templates').update({ name: saveTemplateName, content: sections }).eq('id', saveTemplateOverwriteId);
                    } else {
                      await supabase.from('vacancy_templates').insert({ client_id: vacancy.clientId, name: saveTemplateName, content: sections });
                    }
                    setShowSaveTemplateModal(false);
                  } catch(e) {
                    console.error('Save template error:', e);
                  } finally {
                    setIsSavingTemplate(false);
                  }
                }}
                className="flex-1 py-3 rounded-xl bg-[#e51b23] text-white font-black text-[10px] uppercase tracking-widest hover:bg-[#B71C1C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSavingTemplate ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saveTemplateOverwriteId ? 'Overschrijven' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SJABLONEN PICKER MODAL */}
      {showTemplatesPickerModal && (
        <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-end bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowTemplatesPickerModal(false)}>
          <div className="bg-white w-full sm:w-[480px] h-full sm:h-auto sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right-8 duration-300" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Sjablonen ophalen</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">{templatePickerList.length} opgeslagen sjablonen</p>
              </div>
              <button onClick={() => setShowTemplatesPickerModal(false)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto">
              {templatePickerList.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <LayoutTemplate size={40} className="mx-auto mb-4 opacity-30" />
                  <p className="text-sm font-bold">Geen sjablonen gevonden</p>
                  <p className="text-xs mt-1">Sla eerst een sjabloon op via de builder</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templatePickerList.map((t: any) => (
                    <button
                      key={t.id}
                      onClick={() => {
                        if (!t.content || !Array.isArray(t.content)) return;
                        const combinedHTML = t.content.map((s: any) => `<h3>${s.title}</h3>${s.content}`).join('\n\n');
                        const updatedData = { ...data, builtVacancySections: t.content, finalVacancyContent: combinedHTML };
                        setData(updatedData);
                        void handleSupabaseSync(true, updatedData);
                        setShowTemplatesPickerModal(false);
                      }}
                      className="w-full flex items-center justify-between px-5 py-4 rounded-xl border border-slate-200 hover:border-[#e51b23] hover:bg-red-50 text-left transition-all group"
                    >
                      <div>
                        <p className="text-sm font-black text-slate-900 group-hover:text-[#e51b23] transition-colors">{t.name}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{Array.isArray(t.content) ? `${t.content.length} secties` : 'Laden...'}</p>
                      </div>
                      <ChevronRight size={16} className="text-slate-300 group-hover:text-[#e51b23] transition-colors" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Call Werfplaats Modal */}
      {showCallWerfplaats && (
        <CallWerfplaatsModal 
          contextInfo={vacancy.title} 
          onClose={() => setShowCallWerfplaats(false)} 
        />
      )}
    </div>
  );
};

export default VacancyFlow;

